import * as Sentry from '@sentry/core'
import {
  deleteField,
  doc,
  serverTimestamp,
  writeBatch,
  type WriteBatch,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import type { DateTime } from 'luxon'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import type { StreamInterface } from 'tricklejs/dist/types'
import { PRE_MEETING_QUESTION_SLIDE_ID } from '../constants'
import type { StaticModelCollection } from '../firestore-mobx/model'
import { createStudentInvitation } from '../firestore/Invitation'
import { getLMSConflicts } from '../firestore/LMSConflict'
import { getRoomPromptTypes } from '../firestore/PromptRubricType'
import {
  batchCreateRoomState,
  batchUpdateRoomState,
  bulkCreateRoomStates,
  bulkScheduleRoomStates,
  bulkUpdateRoomUsers,
  createRoomState,
  unassignRoomUsers,
  fetchRoomStates,
  forceAIProcessing,
  getRoomState,
  getRoomStates,
  leaveRoom,
  removeUserRole,
  roomStateAddUser,
  updateRoomState,
} from '../firestore/RoomState'
import {
  getAllRoomStateAnswersForRoom,
  getRoomStateAnswersForInstructor,
} from '../firestore/RoomStateAnswer'
import {
  fetchRoomStateEngagementCountForAssignment,
  getRoomStateEngagementStreamForAssignment,
  getRoomStateEngagementStreamForRoomState,
} from '../firestore/RoomStateEngagement'
import {
  getRoomStateProfessorFeedbackStreamForAssignment,
  getRoomStateProfessorFeedbackStreamForRoom,
} from '../firestore/RoomStateProfessorFeedback'
import {
  deleteRoomState,
  deleteRoomStates,
  resetRoomState,
  resetRoomStates,
} from '../firestore/RoomStateReset'
import {
  getRoomStateRubricResultStreamForAssignment,
  getRoomStateRubricResultStreamForRoom,
} from '../firestore/RoomStateRubricResult'
import {
  getRoomStateRubricResultDetailsForAssignment,
  getRoomStateRubricResultDetailsForInstructor,
} from '../firestore/RoomStateRubricResultDetail'
import {
  getRoomStateSummary,
  getRoomStateSummaryStreamForAssignment,
} from '../firestore/RoomStateSummary'
import { getSection } from '../firestore/Section'
import {
  AssignmentGroupingType,
  AssignmentState,
  batchUpdateSectionAssignmentGroupingSize,
  deleteSectionAssignment,
  type FirestoreSectionAssignment,
  getAssignmentWithLatestExpiresAt,
  getSectionAssignment,
  getSectionAssignments,
  sendTestInstructorEmail,
  touchAssignment,
  updateSectionAssignment,
  updateSectionAssignmentGradingScalars,
  updateSectionAssignmentLTIGradingLineItemId,
  updateSectionAssignmentState,
} from '../firestore/SectionAssignment'
import { setPeerReviewQuestionsForAssignment } from '../firestore/SectionAssignmentPeerReviewQuestion'
import { getSectionAssignmentSlidesStream } from '../firestore/SectionAssignmentSlides'
import { getLatestSectionRequestBySectionId } from '../firestore/SectionRequest'
import { fetchSlides } from '../firestore/Slide'
import { fetchSlideDecksById, getSlideDeck } from '../firestore/SlideDeck'
import { fetchSlideDeckMaterialsForInstructor } from '../firestore/SlideDeckMaterial'
import { fetchSlideDeckRoles } from '../firestore/SlideDeckRole'
import { getSlideQuestions } from '../firestore/SlideQuestion'
import { getSlideRubrics } from '../firestore/SlideRubric'
import {
  getAssignmentRubricsStream,
  type FirestoreAssignmentRubric,
} from '../firestore/AssignmentRubric'
import { AssignmentRubric } from '../models/AssignmentRubric'
import { fetchUserProfiles } from '../firestore/UserProfile'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { LMSConflict } from '../models/LMSConflict'
import { PromptRubricType } from '../models/PromptRubricType'
import { PublicUser } from '../models/PublicUser'
import { RoomState, RoomStateStatus } from '../models/RoomState'
import { RoomStateAnswer } from '../models/RoomStateAnswer'
import { RoomStateEngagement } from '../models/RoomStateEngagement'
import { RoomStateProfessorFeedback } from '../models/RoomStateProfessorFeedback'
import { RoomStateRubricResult } from '../models/RoomStateRubricResult'
import { RoomStateRubricResultDetail } from '../models/RoomStateRubricResultDetail'
import { RoomStateSummary } from '../models/RoomStateSummary'
import { Section, SectionStatus } from '../models/Section'
import { AssignmentType, SectionAssignment } from '../models/SectionAssignment'
import { SectionAssignmentSlides } from '../models/SectionAssignmentSlides'
import { SectionRequest } from '../models/SectionRequest'
import { SlideDeck } from '../models/SlideDeck'
import { SlideDeckMaterial } from '../models/SlideDeckMaterial'
import { SlideDeckRole } from '../models/SlideDeckRole'
import { SlideModel } from '../models/SlideModel'
import { SlideQuestion } from '../models/SlideQuestion'
import { SlideQuestionType } from '../models/SlideQuestionType'
import { RubricTypeClass, SlideRubric } from '../models/SlideRubric'
import { generateUniqueGroupNames } from '../sources-of-truth/defaultGroupNames'
import { getGroupSizeConstraintsFromRoles } from '../sources-of-truth/getGroupSizeConstraintsFromRoles'
import { getRoleIssueForRoomState } from '../sources-of-truth/getRoleIssueForRoomState'
import { getMinGroupSizeIssue } from '../sources-of-truth/getMinGroupSizeIssue'
import {
  getQuizAnswerScore,
  calculateQuizScoreForUser,
  calculateTotalRubricScoreForUserFloat,
  calculateEngagementScoreForUser,
  calculateAttendanceScoreForUser,
  calculateScalarRawScoreDataForUser,
  calculateMedianEngagementDuration,
  isMeetingQuestionScorable,
} from '../sources-of-truth/scoring'
import { sortQuestions } from '../sources-of-truth/sortQuestions'
import { sortRoomStatesWithStatus } from '../sources-of-truth/sortRoomStatesWithStatus'
import { sortRubrics } from '../sources-of-truth/sortRubrics'
import type {
  InvitationType,
  RoomStateWithStatusAndRoleIssueAndMinGroupSizeIssue,
} from '../types'
import { guardOn } from '../util/guardOn'
import { observeRoom } from '../util/observe'
import { shuffle } from '../util/shuffle'
import { compareNames, type NameSortMode } from '../util/nameSort'
import { Cubit } from './core'
import { InstructorChatController } from './InstructorAssignment/InstructorChatController'
import { InstructorPeerReviewController } from './InstructorAssignment/InstructorPeerReviewController'
import { InstructorAssignmentConversationsCubit } from './InstructorAssignmentConversationsCubit'

export const InstructorAssignmentTabs = {
  Groups: 'Groups',
  Summary: 'Summary',
  Results: 'Results',
  Students: 'Students',
  Materials: 'Materials',
  Conversation: 'Conversation',
} as const
export type InstructorAssignmentTab =
  (typeof InstructorAssignmentTabs)[keyof typeof InstructorAssignmentTabs]
class UnauthorizedInstructorAssignmentAccessError extends Error {
  name = 'UnauthorizedInstructorAssignmentAccessError'
}

export type GroupImportErrorCode =
  | 'INVALID_INPUT'
  | 'NOT_ELIGIBLE'
  | 'INTERNAL_ERROR'

export class GroupImportError extends Error {
  name = 'GroupImportError'
  code: GroupImportErrorCode

  constructor(message: string, code: GroupImportErrorCode) {
    super(message)
    this.code = code
  }
}

export const InstructorAssignmentCubitResultsTabs = {
  PeerReview: 'PeerReview',
  Questions: 'Questions',
  Rubric: 'Rubric',
  Polls: 'Polls',
  Quiz: 'Quiz',
} as const
export type InstructorAssignmentCubitResultsTab =
  (typeof InstructorAssignmentCubitResultsTabs)[keyof typeof InstructorAssignmentCubitResultsTabs]

export class InstructorAssignmentCubit extends Cubit {
  repository: FirebaseRepository
  sectionId: string
  assignmentId: string
  roomStateId?: string

  /**
   * Engagement count used by the component to compute a dynamic `defaultTab`.
   * Loaded in `initializeViewStreams`. `undefined` while loading.
   */
  @observable roomStateEngagementCount: number | undefined = undefined

  @observable
  gradeBookSortingStrategy: InstructorAssignmentGradeBookSortingStrategy =
    'fullName'

  @observable gradeBookSortingOrder: 'asc' | 'desc' = 'asc'
  @observable gradeBookNameSortMode: NameSortMode = 'last'

  @observable questionsNameSortMode: NameSortMode = 'last'
  @observable questionsNameSortOrder: 'asc' | 'desc' = 'asc'

  private _slideDeckByIdForOtherAssignments = observable.map<
    string,
    SlideDeck
  >()

  section: Section
  assignment: SectionAssignment
  sectionAssignments: StaticModelCollection<SectionAssignment>
  latestSectionRequest: SectionRequest

  roomStates: StaticModelCollection<RoomState>
  roomStateAnswers: StaticModelCollection<RoomStateAnswer>
  roomStateSummaries: StaticModelCollection<RoomStateSummary>
  roomStateRubricResults: StaticModelCollection<RoomStateRubricResult>
  roomStateEngagements: StaticModelCollection<RoomStateEngagement>
  roomStateProfessorFeedbacksCollection: StaticModelCollection<RoomStateProfessorFeedback>
  roomStateRubricResultDetails: StaticModelCollection<RoomStateRubricResultDetail>
  lmsConflicts: StaticModelCollection<LMSConflict>
  slideDeck: SlideDeck
  slideDeckSlides: StaticModelCollection<SlideModel>
  slideRubrics: StaticModelCollection<SlideRubric>
  sectionAssignmentSlides: StaticModelCollection<SectionAssignmentSlides>
  questionsCollection: StaticModelCollection<SlideQuestion>
  materials: StaticModelCollection<SlideDeckMaterial>
  roles: StaticModelCollection<SlideDeckRole>
  rubricTypes: StaticModelCollection<PromptRubricType>
  assignmentRubrics: StaticModelCollection<AssignmentRubric>
  /** Set when the assignment_rubric stream errors (e.g. a permission-denied
   * read). Lets the UI show an explicit error instead of spinning forever or
   * silently falling back to defaults (which a save could then clobber). */
  @observable assignmentRubricsLoadFailed = false
  assignmentWithLatestExpiresAt: SectionAssignment

  questionFilters = observable.array<string>([])
  userAndRoomFilters = observable.array<string>([])

  peerReviewController: InstructorPeerReviewController

  conversationsCubit: InstructorAssignmentConversationsCubit
  private chatControllers = observable.map<string, InstructorChatController>()

  gradebookPageSize = 20

  /** headless means it's used by another cubit */
  headless = false

  private onSectionFetchPermissionError?: () => void

  constructor(
    repository: FirebaseRepository,
    params: {
      sectionId: string
      assignmentId: string
      initialSection?: Section
      initialSectionAssignment?: SectionAssignment
      roomStateId?: string
      // headless means it's used by another cubit
      headless?: boolean
      onSectionFetchPermissionError?: () => void
    }
  ) {
    super()
    makeObservable(this)

    this.repository = repository
    this.sectionId = params.sectionId
    this.assignmentId = params.assignmentId
    this.roomStateId = params.roomStateId
    this.onSectionFetchPermissionError = params.onSectionFetchPermissionError

    this.headless = params.headless || false

    this.sectionAssignmentSlides =
      SectionAssignmentSlides.emptyCollection(repository)
    this.roomStateAnswers = RoomStateAnswer.emptyCollection(repository)
    this.questionsCollection = SlideQuestion.emptyCollection(repository)
    this.roomStates = RoomState.emptyCollection(repository)
    this.roomStateSummaries = RoomStateSummary.emptyCollection(repository)

    this.roomStateRubricResults =
      RoomStateRubricResult.emptyCollection(repository)
    this.roomStateRubricResultDetails =
      RoomStateRubricResultDetail.emptyCollection(repository)
    this.slideDeck = SlideDeck.empty(repository)
    this.slideDeckSlides = SlideModel.emptyCollection(repository)
    this.slideRubrics = SlideRubric.emptyCollection(repository)
    this.roomStateEngagements = RoomStateEngagement.emptyCollection(repository)
    this.roomStateProfessorFeedbacksCollection =
      RoomStateProfessorFeedback.emptyCollection(repository)
    this.lmsConflicts = LMSConflict.emptyCollection(repository)
    this.materials = SlideDeckMaterial.emptyCollection(repository)
    this.sectionAssignments = SectionAssignment.emptyCollection(repository)
    this.latestSectionRequest = SectionRequest.empty(repository)
    this.assignmentWithLatestExpiresAt = SectionAssignment.empty(repository)
    this.roles = SlideDeckRole.emptyCollection(repository)
    this.rubricTypes = PromptRubricType.emptyCollection(repository)
    this.assignmentRubrics = AssignmentRubric.emptyCollection(repository)

    if (params.initialSection) {
      this.section = params.initialSection
    } else {
      this.section = Section.empty(repository)
    }

    if (
      params.initialSectionAssignment &&
      params.initialSectionAssignment.isLoaded
    ) {
      this.assignment = params.initialSectionAssignment
    } else {
      this.assignment = SectionAssignment.empty(repository)
    }

    this.peerReviewController = new InstructorPeerReviewController(repository, {
      sectionId: this.sectionId,
      assignmentId: this.assignmentId,
      peerReviewExcludesSelfReview:
        this.assignment.data.peerReviewExcludesSelfReview ?? false,
      roomStates: this.roomStates,
    })

    this.conversationsCubit = new InstructorAssignmentConversationsCubit(
      repository,
      {
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
      }
    )
  }

  public initialized = false
  initialize() {
    if (this.initialized) return
    this.initialized = true

    this.startSectionStream()
  }

  startOtherStreams() {
    this.startAssignmentsStream()
    this.startAssignmentStream()
    this.startAssignmentSlidesStream()
    this.startAssignmentWithLatestExpiresAtStream()

    if (this.assignment.isLoaded) {
      this.initializeAssignmentStreams()
    }

    this.conversationsCubit.initialize()

    this.startRoomStatesStream()

    this.initializeViewStreams()

    this.startRubricPromptTypesStream()

    this.startAssignmentRubricsStream()
  }

  startRubricPromptTypesStream() {
    this.addStream(getRoomPromptTypes(this.repository), (rubricTypes) => {
      this.rubricTypes.replaceModels(rubricTypes)
    })
  }

  startAssignmentRubricsStream() {
    // Idempotent: safe to call from both startOtherStreams() (full init) and
    // ensureAssignmentStreams() (output-backed cubits opened from the listing).
    // On error `defaultError` removes the named stream, so a reopen re-subscribes
    // (the "close and reopen to retry" path).
    const streamName = 'assignmentRubricsStream'
    if (this.hasStream(streamName)) return
    this.addStream(
      getAssignmentRubricsStream(this.repository, {
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
      }),
      (rubrics) => {
        runInAction(() => {
          this.assignmentRubricsLoadFailed = false
          this.assignmentRubrics.replaceModels(rubrics)
        })
      },
      {
        name: streamName,
        onError: () => {
          runInAction(() => {
            this.assignmentRubricsLoadFailed = true
          })
        },
      }
    )
  }

  /**
   * Custom rubric config keyed by rubricId, used by scoring to apply per-role
   * weights + scoreMapping. Returns undefined unless the assignment is gated
   * into custom rubrics (`usesCustomRubrics`), so scoring falls back to the
   * legacy equal-weight average for every other assignment.
   */
  @computed
  get customRubricsById(): Map<string, FirestoreAssignmentRubric> | undefined {
    if (!this.assignment.data.usesCustomRubrics) return undefined
    const map = new Map<string, FirestoreAssignmentRubric>()
    this.assignmentRubrics.models.forEach((rubric) => {
      map.set(rubric.id, rubric.data)
    })
    return map
  }

  startSectionStream() {
    this.addStream(
      getSection(this.repository, { sectionId: this.sectionId }),
      (section) => {
        this.section.replaceModel(section)

        this.startOtherStreams()

        const readOnly = section.isReadOnlyShareableSectionForCurrentUser
        const latestSectionRequestKey = 'latestSectionRequestStream'
        if (readOnly || this.hasStream(latestSectionRequestKey)) return

        this.addStream(
          getLatestSectionRequestBySectionId(this.repository, this.sectionId),
          (sectionRequest) => {
            this.latestSectionRequest.replaceModel(sectionRequest)
          },
          { name: latestSectionRequestKey }
        )
      },
      {
        disableCaptureException: true,
        name: 'section',
        onError: (error) => {
          if (
            this.onSectionFetchPermissionError &&
            'code' in error &&
            error.code === 'permission-denied'
          ) {
            Sentry.captureException(
              new UnauthorizedInstructorAssignmentAccessError(
                'Permission denied when fetching section'
              ),
              {
                extra: {
                  sectionId: this.sectionId,
                  assignmentId: this.assignmentId,
                },
              }
            )
            this.onSectionFetchPermissionError()
          } else {
            Sentry.captureException(error)
          }
        },
      }
    )
  }

  startAssignmentsStream() {
    this.addStream(
      getSectionAssignments(this.repository, {
        sectionId: this.sectionId,
      }),
      (assignments) => this.sectionAssignments.replaceModels(assignments)
    )
  }

  startAssignmentStream() {
    this.addStream(
      getSectionAssignment(this.repository, {
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
      }),
      (assignment) => {
        this.assignment.replaceModel(assignment)

        if (
          assignment.isLoaded &&
          assignment.data.peerReviewExcludesSelfReview !== undefined
        ) {
          this.peerReviewController.peerReviewExcludesSelfReview =
            assignment.data.peerReviewExcludesSelfReview
        }

        this.initializeAssignmentStreams()
      }
    )
  }

  startAssignmentSlidesStream() {
    this.addStream(
      getSectionAssignmentSlidesStream(this.repository, {
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
      }),
      (slides) => {
        this.sectionAssignmentSlides.replaceModels(slides)
      }
    )
  }

  startAssignmentWithLatestExpiresAtStream() {
    this.addStream(
      getAssignmentWithLatestExpiresAt(this.repository, {
        sectionId: this.sectionId,
      }),
      (assignment) => {
        this.assignmentWithLatestExpiresAt.replaceModel(assignment)
      },
      { name: 'latest-assignment' }
    )
  }

  startRoomStatesStream() {
    let stream: StreamInterface<RoomState[]>
    if (this.roomStateId) {
      stream = getRoomState(this.repository, {
        roomStateId: this.roomStateId,
      }).map((roomState) => (roomState ? [roomState] : []))
    } else {
      stream = getRoomStates(this.repository, {
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
      })
    }

    this.addStream(stream, (roomStates) =>
      this.roomStates.replaceModels(roomStates)
    )
  }

  startRoomStateAnswersStream() {
    let stream: StreamInterface<RoomStateAnswer[]>
    if (this.roomStateId) {
      stream = getAllRoomStateAnswersForRoom(this.repository, {
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
        roomId: this.roomStateId,
      })
    } else {
      stream = getRoomStateAnswersForInstructor(this.repository, {
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
      })
    }

    this.addStream(stream, (roomStateAnswers) => {
      this.roomStateAnswers.replaceModels(roomStateAnswers)
    })
  }

  startRoomStateRubricsStream() {
    let stream: StreamInterface<RoomStateRubricResult[]>
    if (this.roomStateId) {
      stream = getRoomStateRubricResultStreamForRoom(this.repository, {
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
        roomId: this.roomStateId,
      })
    } else {
      stream = getRoomStateRubricResultStreamForAssignment(this.repository, {
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
      })
    }

    this.addStream(stream, (roomStateRubricResults) => {
      this.roomStateRubricResults.replaceModels(roomStateRubricResults)
    })
  }

  startRoomStateSummaryStream() {
    let stream: StreamInterface<RoomStateSummary[]>
    if (this.roomStateId) {
      stream = getRoomStateSummary(this.repository, {
        roomId: this.roomStateId,
      }).map((summary) => (summary ? [summary] : []))
    } else {
      stream = getRoomStateSummaryStreamForAssignment(this.repository, {
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
      })
    }

    this.addStream(stream, (roomStateSummaries) => {
      this.roomStateSummaries.replaceModels(roomStateSummaries)
    })
  }

  startRoomStateEngagementStream() {
    let stream: StreamInterface<RoomStateEngagement[]>
    if (this.roomStateId) {
      stream = getRoomStateEngagementStreamForRoomState(this.repository, {
        roomId: this.roomStateId,
      })
    } else {
      stream = getRoomStateEngagementStreamForAssignment(this.repository, {
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
      })
    }
    this.addStream(
      stream,
      (roomStateEngagements) => {
        this.roomStateEngagements.replaceModels(roomStateEngagements)
      },
      {
        disableCaptureException: true,
        onError: (error) => {
          Sentry.captureException(error, {
            extra: {
              sectionId: this.sectionId,
              assignmentId: this.assignmentId,
              roomStateId: this.roomStateId,
            },
          })
        },
      }
    )
  }

  startRoomStateProfessorFeedbackStream() {
    let stream: StreamInterface<RoomStateProfessorFeedback[]>
    if (this.roomStateId) {
      stream = getRoomStateProfessorFeedbackStreamForRoom(this.repository, {
        roomId: this.roomStateId,
      })
    } else {
      stream = getRoomStateProfessorFeedbackStreamForAssignment(
        this.repository,
        {
          sectionId: this.sectionId,
          assignmentId: this.assignmentId,
        }
      )
    }
    this.addStream(stream, (feedbacks) => {
      this.roomStateProfessorFeedbacksCollection.replaceModels(feedbacks)
    })
  }

  startRoomStateRubricResultDetailsStream() {
    let stream: StreamInterface<RoomStateRubricResultDetail[]>
    if (this.roomStateId) {
      stream = getRoomStateRubricResultDetailsForInstructor(this.repository, {
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
        roomId: this.roomStateId,
      })
    } else {
      stream = getRoomStateRubricResultDetailsForAssignment(this.repository, {
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
      })
    }
    this.addStream(stream, (rubricResultDetails) => {
      this.roomStateRubricResultDetails.replaceModels(rubricResultDetails)
    })
  }

  // this starts streams needed for various views - not used in headless mode
  initializeViewStreams() {
    if (this.headless) return

    fetchRoomStateEngagementCountForAssignment(this.repository, {
      sectionId: this.sectionId,
      assignmentId: this.assignmentId,
    })
      .then((count) => {
        runInAction(() => {
          this.roomStateEngagementCount = count
        })
      })
      .catch(Sentry.captureException)

    this.initializeAnswers()
  }

  initializeGradebookView() {
    this.initializeAnswers()
    this.startLMSConflictStreams()
  }

  @action
  async startLMSConflictStreams() {
    this.addStream(
      getLMSConflicts(this.repository, {
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
      }),
      (conflicts) => {
        this.lmsConflicts.replaceModels(conflicts)
      }
    )
  }

  initializeResultsView() {
    this.initializeAnswers()
  }

  @computed
  get getResultsTabs() {
    return guardOn(
      // NOTE: If you add another dependency here, you should fire tryInitializeResultsTab after it loads in.
      this.questionsCollection.isLoaded &&
        this.slideRubrics.isLoaded &&
        this.peerReviewController.peerReviewsEnabled.isLoaded,
      () => {
        const availableTabs: InstructorAssignmentCubitResultsTab[] = []

        if (this.peerReviewController.peerReviewsEnabled.value === true) {
          availableTabs.push(InstructorAssignmentCubitResultsTabs.PeerReview)
        }
        availableTabs.push(InstructorAssignmentCubitResultsTabs.Questions)

        if (this.hasRubrics) {
          availableTabs.push(InstructorAssignmentCubitResultsTabs.Rubric)
        }

        if (this.hasPolls) {
          availableTabs.push(InstructorAssignmentCubitResultsTabs.Polls)
        }

        if (this.hasQuizzes) {
          availableTabs.push(InstructorAssignmentCubitResultsTabs.Quiz)
        }

        return availableTabs
      }
    )
  }

  initializeSummaryView() {
    this.initializeAnswers()
  }

  answersInitialized = false
  initializeAnswers() {
    if (this.answersInitialized) return
    this.answersInitialized = true

    this.startRoomStateAnswersStream()
    this.startRoomStateSummaryStream()
    this.startRoomStateRubricsStream()
    this.startRoomStateEngagementStream()
    this.startRoomStateProfessorFeedbackStream()
    this.startRoomStateRubricResultDetailsStream()
    this.peerReviewController.initialize()
  }

  dispose() {
    for (const controller of this.chatControllers.values()) {
      controller.dispose()
    }
    this.chatControllers.clear()

    clearTimeout(this._recomputeRoomStatesWithStatusTimer)

    this.peerReviewController.dispose()
    this.conversationsCubit.dispose()
    return super.dispose()
  }

  // this is guarded, because the slideDeckId cannot change on the assignment
  private initializedAssignmentStreams = false
  initializeAssignmentStreams() {
    if (this.initializedAssignmentStreams) return
    this.initializedAssignmentStreams = true

    const slideDeckId = this.assignment.data.slideDeckId

    this.addStream(
      getSlideQuestions(this.repository, { slideDeckId }),
      (questions) => {
        this.questionsCollection.replaceModels(questions)
      }
    )

    this.addStream(
      getSlideRubrics(this.repository, { slideDeckId }),
      (slideRubrics) => {
        this.slideRubrics.replaceModels(slideRubrics)
      }
    )

    this.addStream(
      getSlideDeck(this.repository, { slideDeckId }),
      (slideDeck) => this.slideDeck.replaceModel(slideDeck)
    )

    fetchSlides(this.repository, {
      slideDeckId: slideDeckId,
    })
      .then((slides) => {
        this.slideDeckSlides.replaceModels(slides)
      })
      .catch((e) => {
        Sentry.captureException(e)
      })

    fetchSlideDeckRoles(this.repository, {
      slideDeckId: slideDeckId,
    })
      .then((roles) => {
        this.roles.replaceModels(roles)
      })
      .catch((e) => {
        Sentry.captureException(e)
      })

    try {
      fetchSlideDeckMaterialsForInstructor(this.repository, {
        slideDeckId: slideDeckId,
      })
        .then((materials) => {
          this.materials.replaceModels(materials)
        })
        .catch((e) => {
          Sentry.captureException(e)
        })
    } catch (e) {
      console.error(e)
    }

    this.peerReviewController.initialize()
  }

  async ensureAssignmentStreams() {
    this.initializeAssignmentStreams()
    // Cubits backed by a precomputed output are never fully initialize()d, so
    // startOtherStreams() (and with it the assignment_rubric stream) never ran.
    // Start it here so the grading dialog's custom-rubric scoreMappings load
    // when opened straight from the section listing. It self-guards via
    // hasStream, so this is a no-op when the stream is already running.
    this.startAssignmentRubricsStream()

    const work = true
    const start = Date.now()

    while (work) {
      const areReady =
        this.questionsCollection.isLoaded &&
        this.slideRubrics.isLoaded &&
        this.slideDeck.isLoaded &&
        this.slideDeckSlides.isLoaded &&
        this.materials.isLoaded

      if (areReady) return true

      await new Promise((resolve) => setTimeout(resolve, 250))

      // timeout after 10s
      if (Date.now() - start > 10000) return false
    }
  }

  cancelSectionAssignment = async () => {
    return await updateSectionAssignmentState(this.repository, {
      sectionId: this.sectionId,
      assignmentId: this.assignmentId,
      state: AssignmentState.canceled,
    })
  }

  deleteSectionAssignment = async () => {
    return await deleteSectionAssignment(this.repository, {
      sectionId: this.sectionId,
      assignmentId: this.assignmentId,
    })
  }

  @action
  async createRoomState(name?: string) {
    const instructor = this.section.instructor
    const isCorre = this.repository.breakoutUser?.isCorre || false

    const persist =
      isCorre ||
      instructor.id === this.repository.currentUser?.uid ||
      !!this.repository.breakoutUser?.isAssistantOfInstructor(instructor.id)

    if (!name) {
      const existingNames = this.roomStates.models.map(
        (room) => room.roomStateName
      )
      const groupNames = generateUniqueGroupNames(
        this.repository,
        1,
        existingNames
      )
      name = groupNames[0]
    }

    await createRoomState(this.repository.firestore, {
      sectionId: this.sectionId,
      assignmentId: this.assignmentId,
      slideDeckId: this.assignment.data.slideDeckId,
      name,
      deletable: !persist,
    })

    this.touchAssignment()
  }

  /**
   * Called by the component via useEffect whenever the URL tab changes.
   * Preserves the side effect from the old `changeTab` setter (clearing
   * filters).
   */
  @action
  onTabChanged() {
    this.userAndRoomFilters.clear()
  }

  @action
  addQuestionFilter = (filter: string) => {
    this.questionFilters.push(filter)
  }

  resetAllGroups = () => {
    return resetRoomStates(this.repository, {
      roomIds: this.roomStates.models.map((roomState) => roomState.id),
      sectionId: this.sectionId,
      assignmentId: this.assignmentId,
    })
  }

  unassignAllGroups = async () => {
    const roomStatesWithStatus = this.roomStatesWithStatus.value
    if (!roomStatesWithStatus) return

    const roomIdsToUnassign = roomStatesWithStatus
      .filter(
        (r) =>
          r.status !== RoomStateStatus.completed &&
          r.status !== RoomStateStatus.queuedForGrading &&
          r.status !== RoomStateStatus.live
      )
      .map((r) => r.id)

    if (roomIdsToUnassign.length > 0) {
      await unassignRoomUsers(this.repository.firestore, roomIdsToUnassign)
    }

    if (
      this.assignment.data.groupingType ===
      AssignmentGroupingType.automaticRandom
    ) {
      await updateSectionAssignment(this.repository, {
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
        groupingType: AssignmentGroupingType.manual,
      })
    }

    await this.touchAssignment()
  }

  resetGroup = async (roomId: string) => {
    await resetRoomState(this.repository, {
      roomId,
      sectionId: this.sectionId,
      assignmentId: this.assignmentId,
      userId: this.repository.uid,
    })
  }

  createStudentInvitation = async ({ type }: { type: InvitationType }) => {
    const doc = await createStudentInvitation(this.repository, {
      sectionId: this.section.id,
      type: type,
    })

    return doc.id
  }

  /**
   * restart ai run, should only be invoked by corre users
   * @param roomId the roomId of the room to reset ai
   * @returns {Promise<boolean>} **true** if restarted, **false** if already in progress
   */
  forceAIProcessing = (roomId: string) =>
    forceAIProcessing(this.repository, { roomId })

  @action
  removeQuestionFilter = (filter: string) => {
    this.questionFilters.remove(filter)
  }

  addUserAndRoomFilter = (filter: string) => {
    this.userAndRoomFilters.push(filter)
  }

  @action
  removeUserAndRoomFilter = (filter: string) => {
    this.userAndRoomFilters.remove(filter)
  }

  @action
  copyGroupsToOtherAssignments = async (
    targetAssignments: SectionAssignment[]
  ) => {
    if (!targetAssignments.length) return

    if (!this.currentAssignmentRoomsEligibleToBeCopied)
      throw new ApplyGroupsToFutureAssignmentsEligibilityError(
        'assignment',
        this.assignmentId
      )

    const assignmentsToUpdate = targetAssignments

    // sort by updated at for deterministic processing order
    const roomStates = this.roomStates.models.sort((a, b) => {
      const [aUpdated, bUpdated] = [
        a.data.updatedAt?.getTime(),
        b.data.updatedAt?.getTime(),
      ]

      if (aUpdated && bUpdated) return bUpdated - aUpdated
      if (aUpdated) return -1
      if (bUpdated) return 1
      return 0
    })

    const removedUsers = this.section.data.removedUserIds || []

    const { payloads } = roomStates.reduce<{
      payloads: Array<{
        userIds: string[]
        groupLeaderUserIds: string[]
        roomName: string
      }>
      usersSeenSoFar: Set<string>
    }>(
      (acc, room) => {
        // place users in one room only
        // ignore removed users
        const newUserIds = room.userIds.filter(
          (id) => !acc.usersSeenSoFar.has(id) && !removedUsers.includes(id)
        )

        // group leaders must be subset of above
        const groupLeaderUserIds = room.groupLeaderUserIds.filter((id) =>
          newUserIds.includes(id)
        )

        if (!newUserIds.length) return acc // if all the users were in another room, skip

        acc.payloads.push({
          userIds: newUserIds,
          groupLeaderUserIds,
          roomName: room.roomStateName,
        })

        newUserIds.forEach((id) => acc.usersSeenSoFar.add(id))

        return acc
      },
      { payloads: [], usersSeenSoFar: new Set() }
    )

    const batchManager = new GroupsExportBatchManager({
      repository: this.repository,
      sectionId: this.sectionId,
    })

    for (const assignment of assignmentsToUpdate) {
      const [sourceGroupingSize, targetGroupingSize] = [
        this.assignment.data.groupingSize,
        assignment.data.groupingSize,
      ]

      // if the source grouping size is larger than the target grouping size
      // we need to update the target grouping size
      if (
        sourceGroupingSize &&
        targetGroupingSize &&
        sourceGroupingSize > targetGroupingSize
      ) {
        const { groupingSize, groupingSizeMaximum } = this.assignment.data

        // take maximum/desires grouping size from source assignment (currently same value)
        batchManager.addUpdateAssignmentGroupingSizeToBatch({
          assignment,
          // don't update this intentionally, could be invalid for roles
          groupingSizeMinimum: undefined,
          groupingSize,
          groupingSizeMaximum,
        })
      }

      const [roomsForAssignment, rolesForAssignment] = await Promise.all([
        fetchRoomStates(this.repository, {
          sectionId: this.sectionId,
          assignmentId: assignment.id,
        }),
        fetchSlideDeckRoles(this.repository, {
          slideDeckId: assignment.data.slideDeckId,
        }),
      ])

      const assignmentStillEligibleToCopyTo =
        assignmentIsEligibleForCopyingRoomsTo({
          assignment,
          currentAssignmentId: this.assignmentId,
          currentSectionId: this.sectionId,
        })

      const roomsStillEligible = assignmentRoomsAreEligibleForCopying({
        targetAssignment: assignment,
        targetAssignmentRooms: roomsForAssignment,
        currentAssignmentRooms: this.roomStates.models,
      })

      const roleConstraintsEligible =
        assignmentRoleConstraintsAreEligibleForCopyingTo({
          sourceAssignment: this.assignment,
          targetRoles: rolesForAssignment,
          currentRooms: this.roomStates.models,
        })

      // check for eligibility before we build the request
      // if no longer eligible throw error, catch on client w/ meaningful message (i.e try again)

      if (!assignmentStillEligibleToCopyTo)
        throw new ApplyGroupsToFutureAssignmentsEligibilityError(
          'assignment',
          assignment.id
        )

      if (!roomsStillEligible)
        throw new ApplyGroupsToFutureAssignmentsEligibilityError(
          'rooms',
          assignment.id
        )

      // using rooms error here as we are using
      // room size compared to target assignment roles to determine
      // roles error type doesn't make sense as roles never change, only rooms
      if (!roleConstraintsEligible)
        throw new ApplyGroupsToFutureAssignmentsEligibilityError(
          'rooms',
          assignment.id
        )

      // if there are empty rooms we can shove users into, lets start with those then create additional rooms as needed
      const roomsToUpdate = roomsForAssignment.sort((a, b) => {
        const [aUpdated, bUpdated] = [
          a.data.updatedAt?.getTime(),
          b.data.updatedAt?.getTime(),
        ]

        if (aUpdated && bUpdated) return bUpdated - aUpdated
        if (aUpdated) return -1
        if (bUpdated) return 1
        return 0
      })

      for (const payload of payloads) {
        // pop room to update off the end of arr
        const roomToUpdate = roomsToUpdate.pop()
        batchManager.addCopyRoomNameAndUsersToBatch({
          ...payload,
          assignmentId: assignment.id,
          slideDeckId: assignment.data.slideDeckId,
          roomToUpdate,
        })
      }

      // if there are still rooms left with users, update them to clear them out
      for (const room of roomsToUpdate) {
        // no update needed
        if (room.userIds.length === 0 && room.groupLeaderUserIds.length === 0)
          continue

        // maintain everything but the users, which we empty
        batchManager.addCopyRoomNameAndUsersToBatch({
          userIds: [],
          groupLeaderUserIds: [],
          assignmentId: room.data.assignmentId!,
          slideDeckId: room.data.slideDeckId,
          roomName: room.data.roomStateName!,
          roomToUpdate: room,
        })
      }
    }

    // commit the batch
    await batchManager.commit()
  }

  @computed
  get invitesDisabled() {
    return (
      this.sectionStatus === SectionStatus.completed ||
      this.section.isReadOnlyShareableSectionForCurrentUser ||
      this.section.isLtiCourse ||
      !this.sectionAssignments.models.some((assignment) => assignment.isActive)
    )
  }

  @computed
  get isEligibleForCSVGroupImport(): boolean {
    // cannot import if assignment is expired
    if (this.assignment.expiresAtIsInPast) return false

    // cannot import if assignment is not active
    if (!this.assignment.isActive) return false

    // cannot import to automatic random grouping
    if (
      this.assignment.data.groupingType ===
      AssignmentGroupingType.automaticRandom
    )
      return false

    // if rooms have users and assignedAt is in the past, cannot import
    if (this.userCount > 0 && !this.assignment.assignedAtIsInFuture)
      return false

    return true
  }

  resolveEmailsToUserIds = async (
    emails: string[]
  ): Promise<Record<string, string>> => {
    const resolveEmails = httpsCallable<
      { sectionId: string; emails: string[] },
      { success: boolean; emailToUserId: Record<string, string> }
    >(this.repository.functions, 'resolveEmailsForSection')

    const { data } = await resolveEmails({
      sectionId: this.sectionId,
      emails,
    })

    if (!data.success) {
      return {}
    }

    return data.emailToUserId
  }

  importGroupsFromCSV = async (
    groups: Array<{ groupName: string; emails: string[] }>
  ) => {
    const importGroups = httpsCallable<
      {
        sectionId: string
        assignmentId: string
        groups: Array<{ groupName: string; emails: string[] }>
      },
      {
        success: boolean
        error?: string
        errorCode?: GroupImportErrorCode
        groupsCreated: number
        groupsReused: number
        studentsAssigned: number
        studentsSkipped: number
        unmatchedEmails: string[]
      }
    >(this.repository.functions, 'importGroupsFromCSV')

    const { data } = await importGroups({
      sectionId: this.sectionId,
      assignmentId: this.assignmentId,
      groups,
    })

    if (!data.success) {
      throw new GroupImportError(
        data.error || 'Failed to import groups',
        data.errorCode || 'INTERNAL_ERROR'
      )
    }

    return data
  }

  @computed
  get currentAssignmentRoomsEligibleToBeCopied() {
    // should not copy empty rooms (no groups configured)
    return (
      this.roomStates.models.length > 0 &&
      this.roomStates.models.some((room) => room.userIds.length > 0)
    )
  }

  async getAssignmentsEligibleForCopyingRooms(): Promise<SectionAssignment[]> {
    // if the rooms in this assignment are empty, return empty array
    if (!this.currentAssignmentRoomsEligibleToBeCopied) return []

    // determine which assignments we want to check the room states for
    const assignmentsToCheckRoomsForEligibility =
      this.sectionAssignments.models.filter((assignment) =>
        assignmentIsEligibleForCopyingRoomsTo({
          assignment,
          currentAssignmentId: this.assignmentId,
          currentSectionId: this.sectionId,
        })
      )

    // iterate over each assignment, and work on one set of rooms at a time to limit memory footprint
    const eligibleAssignments: SectionAssignment[] = []

    for (const assignment of assignmentsToCheckRoomsForEligibility) {
      const [rooms, roles] = await Promise.all([
        fetchRoomStates(this.repository, {
          sectionId: this.sectionId,
          assignmentId: assignment.id,
        }),
        fetchSlideDeckRoles(this.repository, {
          slideDeckId: assignment.data.slideDeckId,
        }),
      ])

      const roomsEligible = assignmentRoomsAreEligibleForCopying({
        targetAssignment: assignment,
        targetAssignmentRooms: rooms,
        currentAssignmentRooms: this.roomStates.models,
      })

      const roleConstraintsEligible =
        assignmentRoleConstraintsAreEligibleForCopyingTo({
          targetRoles: roles,
          currentRooms: this.roomStates.models,
          sourceAssignment: this.assignment,
        })

      if (roomsEligible && roleConstraintsEligible)
        eligibleAssignments.push(assignment)
    }

    return eligibleAssignments
  }

  @computed
  get slideDeckByIdForOtherAssignments() {
    return new Map(this._slideDeckByIdForOtherAssignments)
  }

  @computed
  get sectionStatus() {
    const request =
      this.latestSectionRequest.isEmpty || this.latestSectionRequest.isWithdrawn
        ? null
        : this.latestSectionRequest
    return this.section.getSectionStatus({
      assignments: this.sectionAssignments.models,
      latestInvoiceRequest: request,
    })
  }

  @computed
  get hasBeenStarted() {
    return this.roomStates.models.some(
      (roomState) => typeof roomState.data.activeSlide === 'number'
    )
  }

  @computed
  get hasBeenScheduled() {
    return this.roomStates.models.some(
      (roomState) => !!roomState.data.scheduledAt
    )
  }

  @computed
  get allDataLoaded(): boolean {
    return this.allDataExceptStudentsLoaded && this.section.studentsLoaded
  }

  @computed
  get allDataExceptStudentsLoaded(): boolean {
    return (
      this.section.isLoaded &&
      this.sectionAssignmentSlides.isLoaded &&
      this.assignment.isLoaded &&
      this.roomStates.isLoaded &&
      this.roomStateAnswers.isLoaded &&
      this.roomStateSummaries.isLoaded &&
      this.roomStateEngagements.isLoaded &&
      this.roomStateRubricResults.isLoaded &&
      this.slideRubrics.isLoaded &&
      this.questionsCollection.isLoaded &&
      this.sectionAssignments.isLoaded &&
      this.roles.isLoaded &&
      (this.isReadOnly || this.latestSectionRequest.isLoaded)
    )
  }

  @computed
  get assignmentsRubricsRoomStatesDataLoaded(): boolean {
    return (
      this.assignment.isLoaded &&
      this.roomStates.isLoaded &&
      this.roomStateRubricResults.isLoaded &&
      this.slideRubrics.isLoaded
    )
  }

  @computed
  get isReadOnly() {
    return this.section.isReadOnlyShareableSectionForCurrentUser
  }

  @computed
  get userCount(): number {
    return this.roomStates.models.reduce(
      (acc, roomState) => acc + roomState.data.userIds.length,
      0
    )
  }

  @computed
  get roomNamesById(): Map<string, string> {
    const map = new Map<string, string>()
    this.roomStates.models.forEach((room) => {
      if (!room.data.roomStateName) return
      return map.set(room.id, room.data.roomStateName)
    })
    return map
  }

  @computed
  get sectionAssignmentSlidesURL() {
    if (this.sectionAssignmentSlides.models.length > 0) {
      return this.sectionAssignmentSlides.models[0].data.url
    }
  }

  @computed
  get groupCount(): number {
    return this.roomStates.models.length
  }

  @computed
  get completedGroupCount(): number {
    return this.roomStatesStatuses.filter(
      (status) => status === RoomStateStatus.completed
    ).length
  }

  @computed
  get roomStatesStatuses() {
    return this.assignmentGroupData.map((group) => group.roomStateStatus)
  }

  @computed
  get rubricResultsDataIsLoading() {
    return !(this.slideRubrics.isLoaded && this.roomStateRubricResults.isLoaded)
  }

  @computed
  get questionsAnswerDataIsLoading() {
    return (
      !this.roomStateAnswers.isLoaded ||
      !this.questionsCollection.isLoaded ||
      !this.slideDeckSlides.isLoaded
    )
  }

  @computed
  get slideRubricsSorted() {
    return sortRubrics(this.slideRubrics.models, this.slideDeckSlides.models)
  }

  /**
   * Returns a map of the slideRubrics and their results
   */
  @computed
  get rubrics() {
    /// loop over questions and get all those with the type poll
    const rubrics: Map<SlideRubric, RubricResultWithUser[]> = new Map()
    for (const rubric of this.slideRubricsSorted) {
      const rubricResults = this.roomStateRubricResults.models
        .filter((rubricResult) => rubricResult.rubricId === rubric.id)
        .sort((a, b) => b.data.score - a.data.score)
      // get users
      if (rubricResults.length) {
        const resultsWithUsers: RubricResultWithUser[] = rubricResults.map(
          (result) => {
            const user = this.section.studentsIncludingRemoved.find(
              (element) => element.id === result.data.userId
            )
            const resultWithUser = result as RubricResultWithUser
            resultWithUser.user = user || PublicUser.empty(this.repository)
            return resultWithUser
          }
        )
        rubrics.set(rubric, resultsWithUsers)
      } else {
        rubrics.set(rubric, [])
      }
    }
    return rubrics
  }

  @computed
  get assignmentProgress() {
    const studentsOnceInSection = new Set([
      ...this.section.data.userIds,
      ...(this.section.data.removedUserIds ?? []),
    ])

    // count total unique users -- start with section users
    const userIds = new Set<string>(this.section.data.userIds)

    // this will include removed section users in the set if
    // they were in a room for the assignment and not absent
    this.roomStates.models.forEach((roomState) => {
      roomState.data.userIds.forEach((userId) => {
        if (!studentsOnceInSection.has(userId)) return
        userIds.add(userId)
      })
    })

    const studentCount = userIds.size

    // filter out empty groups
    const validGroups = this.roomStates.models.filter(
      (roomState) => roomState.userIds.length > 0
    )

    const totalGroups: number = validGroups.length

    const completedGroups: number = validGroups.reduce(
      (previousValue: number, roomState: RoomState) => {
        if (
          this.roomStateSummaries.models.some(
            (roomStateSummary: RoomStateSummary) =>
              roomStateSummary.data.roomId === roomState.id
          )
        ) {
          return previousValue + 1
        } else {
          return previousValue
        }
      },
      0
    )

    const completedStudentIds = new Set<string>()
    for (const group of this.assignmentGroupData) {
      for (const user of group.users) {
        // count all users who were OR are member of section and have engagement record as completed
        if (!studentsOnceInSection.has(user.id)) continue
        const engagementRecord = group.engagementData.get(user.id)
        const hasEngagement = engagementRecord && engagementRecord.isNotEmpty
        if (hasEngagement) completedStudentIds.add(user.id)
      }
    }

    const completedStudentCount = completedStudentIds.size

    // round to 2 decimal places
    const percentageCompleted: number =
      Math.round((completedStudentCount / Math.max(studentCount, 1)) * 10000) /
      100

    return {
      completedGroups,
      percentageCompleted,
      studentCount,
      totalGroups,
      completedStudentCount,
    }
  }

  @computed
  get assignmentGroupData(): AssignmentGroupData[] {
    const groupData: AssignmentGroupData[] = []

    if (!this.allDataLoaded) return groupData

    const quizQuestions = this.quizQuestions

    const quizQuestionsWithAnswersByRoomId: Record<
      string,
      Map<SlideQuestion, RoomStateAnswer[]>
    > = {}

    // populate the quiz questions by room state with the answers
    quizQuestions.forEach((answers, question) => {
      answers.forEach((answer) => {
        const {
          data: { roomId },
        } = answer
        const questionsAnswersForRoom = quizQuestionsWithAnswersByRoomId[roomId]

        // if entry no entry in record, init with new map
        if (!questionsAnswersForRoom)
          return (quizQuestionsWithAnswersByRoomId[roomId] = new Map([
            [question, [answer]],
          ]))

        // if entry in QA map exists, push answer to it
        if (questionsAnswersForRoom.has(question))
          return questionsAnswersForRoom.get(question)?.push(answer)

        // if no entry in QA map exists, create new entry
        questionsAnswersForRoom.set(question, [answer])
      })
    })

    const sortingQuestions = new Map<string, SlideQuestion>()

    // make AssignmentGroupData instance for each RoomState
    this.roomStates.models.forEach((roomState) => {
      const roomWasGraded = this.roomStateEngagements.models.some(
        ({ data }) => data.roomId === roomState.id
      )

      const roomStateStatus = roomState.getRoomStateStatus(
        this.assignment.expiresAt,
        this.section.data.sectionState,
        this.slideDeckSlides.length,
        this.assignment.data.assignmentState,
        roomWasGraded
      )

      const quizScore = new Map<string, number>()
      const quizScoreByUserIdAndSlideId = new Map<string, Map<string, number>>()
      const sortingQuizScores = new Map<string, Map<string, number>>()
      const groupSortingAnswerScores = new Map<string, number>()

      const questionsAnswersForRoom =
        quizQuestionsWithAnswersByRoomId[roomState.id]

      const usersHaveTakenQuiz = new Set<string>()

      if (questionsAnswersForRoom) {
        questionsAnswersForRoom.forEach((answers, question) => {
          answers.forEach((answer) => {
            const userId = answer.data.userId
            const isGroupAnswer = userId === answer.data.roomId
            const score = getQuizAnswerScore({ answer, question })
            const isSorting =
              question.questionType === SlideQuestionType.sorting

            if (question.isQuiz) usersHaveTakenQuiz.add(userId)

            // if not sorting, add score to user quiz score total
            if (!isSorting) {
              // set on total quiz score map
              quizScore.set(userId, (quizScore.get(userId) ?? 0) + score)

              // update quiz score by userId and slideId
              const slideId = question.data.slideId ?? ''
              if (!quizScoreByUserIdAndSlideId.has(userId))
                quizScoreByUserIdAndSlideId.set(userId, new Map())

              const scoresBySlide = quizScoreByUserIdAndSlideId.get(userId)
              const updatedSlideScore =
                (scoresBySlide?.get(slideId) ?? 0) + score
              scoresBySlide?.set(slideId, updatedSlideScore)
              return
            }

            // if is sorting add score to sorting question collection
            // then add to quiz scores collection for users
            sortingQuestions.set(question.id, question)

            if (isGroupAnswer)
              return groupSortingAnswerScores.set(question.id, score)

            if (!sortingQuizScores.has(userId))
              sortingQuizScores.set(userId, new Map())

            sortingQuizScores.get(userId)?.set(question.id, score)
          })
        })
      }

      const {
        bySlideId: totalMultipleChoiceQuestionsBySlideId,
        total: totalMultipleChoiceQuestions,
      } = Array.from(quizQuestions.keys()).reduce<{
        bySlideId: Map<string, number>
        total: number
      }>(
        (acc, question) => {
          if (question.questionType !== SlideQuestionType.multipleChoice)
            return acc
          acc.total += 1 //increment the total
          const slideId = question.data.slideId ?? ''
          const updatedCount = (acc.bySlideId.get(slideId) ?? 0) + 1
          acc.bySlideId.set(slideId, updatedCount)
          return acc
        },
        { bySlideId: new Map(), total: 0 }
      )

      const roomUsers = [
        ...new Set([
          ...roomState.data.userIds,
          ...(roomState.data.absentUserIds ?? []),
        ]),
      ]

      // divide the sum by total quiz questions for each user to get
      // score as a float 0 - 1
      roomUsers.forEach((userId) => {
        // total score for each user
        {
          const scoreSum = quizScore.get(userId) ?? 0
          const questionCount = totalMultipleChoiceQuestions
          const score = scoreSum / questionCount
          quizScore.set(userId, score)
        }

        // total score for each user by slide
        const forUserBySlide = quizScoreByUserIdAndSlideId.get(userId)
        if (!forUserBySlide) return

        forUserBySlide.forEach((score, slideId) => {
          const questionCount =
            totalMultipleChoiceQuestionsBySlideId.get(slideId) ?? 1
          const slideScore = score / questionCount
          forUserBySlide.set(slideId, slideScore)
        })
      })

      const users: PublicUser[] = []
      const absentUsers: PublicUser[] = []

      for (const userId of roomState.data.userIds) {
        const user = this.section.studentsIncludingRemoved.find(
          (element) => element.id === userId
        )
        if (user) users.push(user)
      }

      for (const userId of roomState.data.absentUserIds || []) {
        const user = this.section.studentsIncludingRemoved.find(
          (element) => element.id === userId
        )
        if (user) absentUsers.push(user)
      }

      // sort the users arr
      users.sort(
        (
          { data: { lastName: lastNameA, firstName: firstNameA } },
          { data: { lastName: lastNameB, firstName: firstNameB } }
        ) =>
          // try sort by lastName first, then firstName
          lastNameA !== lastNameB
            ? lastNameA.localeCompare(lastNameB)
            : firstNameA.localeCompare(firstNameB)
      )

      // sort absent users
      absentUsers.sort(
        (
          { data: { lastName: lastNameA, firstName: firstNameA } },
          { data: { lastName: lastNameB, firstName: firstNameB } }
        ) =>
          lastNameA !== lastNameB
            ? lastNameA.localeCompare(lastNameB)
            : firstNameA.localeCompare(firstNameB)
      )

      const userIds = users.map((user) => user.id)

      const summary =
        this.roomStateSummaries.models.find(
          (element) => element.data.roomId === roomState.id
        ) || RoomStateSummary.empty(this.repository)

      const rubricResults = new Map<
        string,
        Map<SlideRubric, RoomStateRubricResult[]>
      >()

      this.roomStateRubricResults.models.forEach((rubricResult) => {
        if (rubricResult.data.roomId !== roomState.id) return
        const slideRubric =
          this.slideRubrics.models.find(
            (element) => element.id === rubricResult.rubricId
          ) || SlideRubric.empty(this.repository)
        const userId = rubricResult.data.userId

        // init result array for each rubric, but leave empty
        if (!rubricResults.has(userId)) {
          const emptyResults = new Map<SlideRubric, RoomStateRubricResult[]>()
          this.slideRubrics.models.forEach((rubric) =>
            emptyResults.set(rubric, [])
          )
          rubricResults.set(userId, emptyResults)
        }

        const results = rubricResults.get(userId)?.get(slideRubric) || []
        results.push(rubricResult)
        rubricResults.get(userId)?.set(slideRubric, results)
      })

      const engagementData = new Map<string, RoomStateEngagement>()
      this.roomStateEngagements.models.forEach((engagement) => {
        if (engagement.data.roomId === roomState.id) {
          engagementData.set(engagement.data.userId, engagement)
        }
      })

      if (
        users.length === 0 &&
        this.assignment.data.assignmentType === AssignmentType.ManyGroups
      ) {
        return
      }

      groupData.push(
        new AssignmentGroupData({
          absentUsers,
          groupName: roomState.roomStateName,
          groupLeader: roomState.groupLeaderUserId,
          groupMembers: userIds,
          groupSortingAnswerScores,
          roomState,
          roomStateStatus,
          engagementData,
          quizScore,
          quizScoreByUserIdAndSlideId,
          sortingQuizScores,
          sortingQuestions,
          summary,
          rubricResults,
          users: users,
          usersHaveTakenQuiz: usersHaveTakenQuiz,
          promptRubricTypes: this.rubricTypes.models,
          assignmentRubrics: this.customRubricsById,
        })
      )
    })

    return groupData
  }

  @computed
  get assignmentGroupDataSorted(): AssignmentGroupData[] {
    return this.assignmentGroupData
      .concat()
      .sort((a, b) => this.sortAssignmentGroupData(a, b))
  }

  @computed
  get assignmentGroupDataForRoomsWithNoUsers(): AssignmentGroupData[] {
    const groupData: AssignmentGroupData[] = []
    this.roomStates.models.forEach((roomState) => {
      if (roomState.data.userIds.length === 0) {
        const summary =
          this.roomStateSummaries.models.find(
            (element) => element.data.roomId === roomState.id
          ) || RoomStateSummary.empty(this.repository)

        const roomWasGraded = this.roomStateEngagements.models.some(
          (e) => e.data.roomId === roomState.id
        )

        const roomStateStatus = roomState.getRoomStateStatus(
          this.assignment.expiresAt,
          this.section.data.sectionState,
          this.slideDeckSlides.length,
          this.assignment.data.assignmentState,
          roomWasGraded
        )

        // if it's flagged for deletion, and it hasn't started, don't show it
        if (roomState.isFlaggedForDeletion && !roomState.didStart) return

        groupData.push(
          new AssignmentGroupData({
            groupName: roomState.roomStateName,
            groupLeader: roomState.groupLeaderUserId,
            groupMembers: [],
            roomState,
            roomStateStatus,
            engagementData: new Map(),
            quizScore: new Map(),
            sortingQuizScores: new Map(),
            sortingQuestions: new Map(),
            summary,
            rubricResults: new Map(),
            users: [],
            usersHaveTakenQuiz: new Set(),
            promptRubricTypes: this.rubricTypes.models,
          })
        )
      }
    })
    return groupData.sort((a, b) => this.sortAssignmentGroupData(a, b))
  }

  @computed
  get roomStateProfessorFeedbacks() {
    return this.roomStateProfessorFeedbacksCollection.models.sort((a, b) => {
      // sort by updatedAt ascending
      if (a.data.updatedAt && b.data.updatedAt) {
        return a.data.updatedAt.getTime() - b.data.updatedAt.getTime()
      }
      return 0
    })
  }

  // get summary of ProfessorFeedback
  // this is the top 5 feedbacks sorted by length after filtering
  // on sentiment value if any have a sentiment value
  @computed
  get professorFeedbackSummaryList(): RoomStateProfessorFeedback[] {
    // get the top 5 feedbacks sorted by length
    const feedbacks = this.roomStateProfessorFeedbacks
    const output: RoomStateProfessorFeedback[] = feedbacks.filter(
      (feedback) =>
        feedback.data.professorFeedback &&
        feedback.data.professorFeedback.length > 0
    )
    // add all feedbacks to output
    // sort by length of feedback.data.professorFeedback[0]
    const feedbacksSorted = output.sort((a, b) => {
      return (
        b.data.professorFeedback![0].length -
        a.data.professorFeedback![0].length
      )
    })

    return feedbacksSorted.slice(0, 4)
  }

  // count the number of ProfessorFeedback questions
  @computed
  get professorFeedbackQuestionsCount() {
    return this.roomStateProfessorFeedbacks.reduce(
      (acc, feedback) => acc + (feedback.data.professorFeedback?.length ?? 0),
      0
    )
  }

  @computed
  get professorFeedbackDataLoaded() {
    return (
      this.roomStateProfessorFeedbacksCollection.isLoaded &&
      this.section.studentsLoaded
    )
  }

  getSectionUser = (userId: string): PublicUser => {
    const user = this.section.studentsIncludingRemoved.find(
      (u) => u.id === userId
    )
    if (!user) {
      return PublicUser.empty(this.repository)
    }
    return user
  }

  @computed
  get questions() {
    type Question = {
      group: string
      isGroupLeader: boolean
      questions: string[]
      user: PublicUser
    }
    const questions: Question[] = []

    for (const feedback of this.roomStateProfessorFeedbacks) {
      if (!feedback.data.professorFeedback) continue

      const roomState = this.roomStates.models.find(
        (r) => r.id === feedback.data.roomId
      )

      if (!roomState) continue

      const groupName = roomStateName({
        roomState: roomState,
        section: this.section,
        usersMap: Object.fromEntries(
          this.section.studentsIncludingRemoved.map((u) => [u.id, u])
        ),
      })

      // confirm entry.key is in section.users
      const user = this.section.studentsIncludingRemoved.find(
        (u) => u.id === feedback.data.userId
      )

      if (!user) continue

      questions.push({
        group: groupName,
        isGroupLeader: roomState.groupLeaderUserIds.includes(
          feedback.data.userId
        ),
        questions: feedback.data.professorFeedback,
        user,
      })
    }

    // sort by the selected name mode, with group name as the final tiebreaker
    const directionMod = this.questionsNameSortOrder === 'asc' ? 1 : -1
    questions.sort((a, b) => {
      const byName = compareNames(
        a.user.nameParts,
        b.user.nameParts,
        this.questionsNameSortMode
      )
      if (byName !== 0) return byName * directionMod
      return a.group.localeCompare(b.group) * directionMod
    })
    return questions
  }

  get filteredQuestions() {
    if (!this.questionFilters.length) return this.questions
    return this.questions.filter((q) => {
      const user = q.user
      const fullName = user.fullName.toLowerCase()
      const group = q.group.toLowerCase()
      const questions = q.questions
      return this.questionFilters.some((filter) => {
        filter = filter.toLowerCase()
        return (
          fullName.includes(filter) ||
          group.includes(filter) ||
          questions.some((q) => q.toLowerCase().includes(filter))
        )
      })
    })
  }

  @computed
  get polls() {
    /// loop over questions and get all those with the type poll
    const polls: Map<SlideQuestion, Array<RoomStateAnswer>> = new Map()
    for (const question of this.questionsCollection.models) {
      if (
        question.questionType === SlideQuestionType.poll ||
        question.questionType === SlideQuestionType.customPoll
      ) {
        const answers = this.roomStateAnswers.models.filter(
          (ans) =>
            ans.data.slideQuestionId === question.id && ans.data.submitted
        )
        if (answers.length) {
          polls.set(question, answers)
        } else {
          polls.set(question, [])
        }
      }
    }
    return this.sortQuestionsMap(polls)
  }

  @computed
  get hasPolls(): boolean {
    return this.questionsCollection.models.some(
      (question) =>
        question.questionType === SlideQuestionType.poll ||
        question.questionType === SlideQuestionType.customPoll
    )
  }

  // count all the answers for each question
  @computed
  get uniquePollAnswerCount(): number {
    const polls = this.polls
    const counts: Map<string, number> = new Map()
    polls.forEach((answers) => {
      for (const answer of answers) {
        counts.set(
          answer.data.userId,
          (counts.get(answer.data.userId) || 0) + 1
        )
      }
    })

    // return the sum of the counts
    return Array.from(counts.values()).reduce((acc, count) => acc + count, 0)
  }

  @computed
  get assignmentGroupDataSortedWithNotInGroup(): AssignmentGroupData[] {
    const groupDataSorted = this.assignmentGroupDataSorted.concat()
    const userIdsInGroups: string[] = []

    for (const roomState of this.roomStates.models) {
      userIdsInGroups.push(...roomState.data.userIds)
    }

    const usersIdNotInGroups = this.section.data.userIds.filter(
      (userId) => !userIdsInGroups.includes(userId)
    )

    for (const userId of usersIdNotInGroups) {
      const user = this.repository.userStore.getUser(userId)
      if (user) {
        groupDataSorted.push(
          new AssignmentGroupData({
            groupName: '',
            groupMembers: [userId],
            summary: RoomStateSummary.empty(this.repository),
            roomState: RoomState.empty(this.repository),
            roomStateStatus: RoomStateStatus.completed,

            engagementData: new Map(),
            quizScore: new Map(),
            sortingQuizScores: new Map(),

            sortingQuestions: new Map(),
            rubricResults: new Map(),
            users: [user],
            usersHaveTakenQuiz: new Set(),
            promptRubricTypes: this.rubricTypes.models,
          })
        )
      }
    }

    if (this.userAndRoomFilters.length > 0) {
      return groupDataSorted.filter((groupData) => {
        return groupData.users.some((user) =>
          this.userAndRoomFilters.every(
            (filter) =>
              user.fullName.toLowerCase().includes(filter.toLowerCase()) ||
              groupData.groupName?.toLowerCase().includes(filter.toLowerCase())
          )
        )
      })
    }

    return groupDataSorted
  }

  @computed
  get usersNotInGroup(): PublicUser[] {
    const userIdsInGroups: string[] = []

    // Get all the userIds in groups.
    for (const roomState of this.roomStates.models) {
      userIdsInGroups.push(...roomState.data.userIds)
    }

    const users = this.section.students
      // Filter out the users that are in groups.
      .filter((student) => !userIdsInGroups.includes(student.id))
      // Sort the users by first name and last name.
      .sort((a, b) => {
        if (a.data.firstName !== b.data.firstName) {
          return a.data.firstName.localeCompare(b.data.firstName)
        }
        return a.data.lastName.localeCompare(b.data.lastName)
      })

    if (this.userAndRoomFilters) {
      return users.filter((user) =>
        this.userAndRoomFilters.every((filter) =>
          user.fullName.toLowerCase().includes(filter.toLowerCase())
        )
      )
    }

    return users
  }

  @computed
  get emptyGroupWithAllUsersNotInGroup(): AssignmentGroupData {
    return new AssignmentGroupData({
      groupName: '',
      groupMembers: this.usersNotInGroup.map((user) => user.id),
      summary: RoomStateSummary.empty(this.repository),
      roomState: RoomState.empty(this.repository),
      roomStateStatus: RoomStateStatus.completed,
      engagementData: new Map(),
      quizScore: new Map(),
      sortingQuizScores: new Map(),
      sortingQuestions: new Map(),
      rubricResults: new Map(),
      users: this.usersNotInGroup,
      usersHaveTakenQuiz: new Set(),
      promptRubricTypes: this.rubricTypes.models,
    })
  }

  @computed
  get usersMapIncludingInstructorAndRemovedStudents() {
    const map: Map<string, PublicUser> = new Map()
    for (const user of this.section.studentsIncludingRemoved) {
      map.set(user.id, user)
    }
    map.set(this.section.instructor.id, this.section.instructor)
    return map
  }

  get quizQuestions(): Map<SlideQuestion, RoomStateAnswer[]> {
    const quizQuestions: Map<SlideQuestion, RoomStateAnswer[]> = new Map()

    this.questionsCollection.models.forEach((question) => {
      if (question.isQuiz) {
        const answers = this.roomStateAnswers.models.filter(
          (element) =>
            element.data.slideQuestionId === question.id &&
            element.data.submitted
        )
        if (answers.length > 0) {
          quizQuestions.set(question, answers)
        }
      }
    })

    return this.sortQuestionsMap(quizQuestions)
  }

  get quizQuestionsBySlide(): QuizQuestionsBySlide {
    const noSlide: QuizQuestionsBySlide[number] = {
      slide: 'no-slide',
      quizQuestions: [],
    }
    const preMeetingQuiz: QuizQuestionsBySlide[number] = {
      slide: 'pre-meeting-quiz',
      quizQuestions: [],
    }
    const quizQuestionsBySlide: QuizQuestionsBySlide = [noSlide, preMeetingQuiz]

    // Start with slide in order to maintain slide-based ordering
    for (const slide of this.slideDeckSlides.models) {
      quizQuestionsBySlide.push({ slide, quizQuestions: [] })
    }

    for (const [question, answers] of this.quizQuestions.entries()) {
      let slideId: string | null | undefined
      if (question.hasGroupSlideId && question.hasSlideId) {
        slideId = question.data.groupSlideId
      } else if (question.hasSlideId) {
        slideId = question.data.slideId
      } else if (question.hasGroupSlideId) {
        slideId = question.data.groupSlideId
      }
      let slide = quizQuestionsBySlide.find(
        ({ slide }) => typeof slide === 'object' && slide.id === slideId
      )
      if (slideId === 'pre-meeting-quiz') {
        slide = preMeetingQuiz
      }
      if (!slide) {
        slide = noSlide
      }
      slide.quizQuestions.push({ question, answers })
    }

    return quizQuestionsBySlide
  }

  @computed
  get hasQuizzes(): boolean {
    return this.questionsCollection.models.some((question) => question.isQuiz)
  }

  @computed
  get hasScorableQuizzes(): boolean {
    return this.questionsCollection.models.some((question) =>
      isMeetingQuestionScorable(question)
    )
  }

  @computed
  get hasRubrics(): boolean {
    return this.slideRubrics.models.length > 0
  }

  async observeRoom(roomState: RoomState) {
    return observeRoom(this.repository, roomState)
  }

  // get multiple choice questions only
  @computed
  get multipleChoiceQuestions(): Map<SlideQuestion, RoomStateAnswer[]> {
    const quizQuestions: Map<SlideQuestion, RoomStateAnswer[]> = new Map()

    this.questionsCollection.models.forEach((question) => {
      if (question.questionType === SlideQuestionType.multipleChoice) {
        const answers = this.roomStateAnswers.models.filter(
          (element) =>
            element.data.slideQuestionId === question.id &&
            element.data.submitted
        )
        if (answers.length > 0) {
          quizQuestions.set(question, answers)
        }
      }
    })

    return this.sortQuestionsMap(quizQuestions)
  }

  // get the number of answers for each question and unique user
  @computed
  get uniqueMultipleChoiceAnswerCount(): number {
    const multipleChoice = this.multipleChoiceQuestions
    const multipleChoiceValues = Array.from(multipleChoice.values())

    const count = multipleChoiceValues.reduce<number>((acc, answers) => {
      const usersAnswered = new Set(answers.map((a) => a.data.userId)).size
      return acc + usersAnswered
    }, 0)

    return count
  }

  // get question with the most wrong answers
  // this is defined as the answer.data.answer !== question.data.correct
  @computed
  get multipleChoiceQuestionWithMostWrongAnswers(): SlideQuestion | undefined {
    let questionWithMostWrongAnswers: SlideQuestion | undefined
    let maxWrongAnswers = 0

    this.multipleChoiceQuestions.forEach((answers, question) => {
      let wrongAnswers = 0
      for (const answer of answers) {
        if (answer.data.answer !== question.data.correct) {
          wrongAnswers++
        }
      }
      if (wrongAnswers > maxWrongAnswers || !questionWithMostWrongAnswers) {
        maxWrongAnswers = wrongAnswers
        questionWithMostWrongAnswers = question
      }
    })

    return questionWithMostWrongAnswers
  }

  // get the most common wrong answer for the above question
  @computed
  get mostCommonWrongAnswer(): number {
    const multipleChoice = this.multipleChoiceQuestions
    const question = this.multipleChoiceQuestionWithMostWrongAnswers

    if (!question) return 0

    const wrongAnswers: Map<number, number> = new Map()

    multipleChoice.get(question)?.forEach((answer) => {
      if (answer.data.answer !== question.data.correct) {
        wrongAnswers.set(
          answer.data.answer,
          (wrongAnswers.get(answer.data.answer) || 0) + 1
        )
      }
    })

    let mostCommonAnswer = 0
    let maxCount = 0

    wrongAnswers.forEach((count, answer) => {
      if (count > maxCount) {
        maxCount = count
        mostCommonAnswer = answer
      }
    })

    return mostCommonAnswer
  }

  @computed
  get multipleChoicePerformance(): Map<string, number> {
    const performance: Map<string, number> = new Map()
    const multipleChoice = this.multipleChoiceQuestions

    // For each user, calculate their total score across all questions
    this.section.studentsIncludingRemoved.forEach((student) => {
      let totalScore = 0
      let totalQuestions = 0

      multipleChoice.forEach((answers, question) => {
        const userAnswer = answers.find((a) => a.data.userId === student.id)
        if (userAnswer) {
          totalScore += getQuizAnswerScore({ answer: userAnswer, question })
          totalQuestions++
        }
      })

      // Calculate percentage if they answered any questions
      if (totalQuestions > 0) {
        performance.set(student.id, totalScore / totalQuestions)
      } else {
        performance.set(student.id, 0)
      }
    })

    return performance
  }

  @computed
  get multipleChoiceHistogram(): Record<string, number> {
    const histogram: { [key: string]: number } = {
      '20 %': 0,
      '40 %': 0,
      '60 %': 0,
      '80 %': 0,
      '100 %': 0,
    }

    const performance = this.multipleChoicePerformance

    performance.forEach((percentage) => {
      if (percentage < 0.2) {
        histogram['20 %'] += 1
      } else if (percentage < 0.4) {
        histogram['40 %'] += 1
      } else if (percentage < 0.6) {
        histogram['60 %'] += 1
      } else if (percentage < 0.8) {
        histogram['80 %'] += 1
      } else {
        histogram['100 %'] += 1
      }
    })

    return histogram
  }

  /**
   * Returns a map for roomState and the rubric scores.
   * We will calculate the room's score using the same
   * method as getRubricScore but the numerator will be
   * the max score for each rubric within this room
   * and the denominator will be the total number of rubrics.
   */
  @computed
  get rubricScores(): Map<RoomState, number> {
    const roomRubricScores: Map<RoomState, number> = new Map()
    const rubricScoresByRoom: Map<
      string,
      Map<SlideRubric, number[]>
    > = new Map()

    this.rubrics.forEach((rubricResults, rubric) => {
      const thisRubricScoreByRoom: Map<string, number> = new Map()

      rubricResults.forEach((rubricResult) => {
        // if rubricResult.score.index is larger than the rubricScoresByRoom
        // then add it to the map

        if (
          !thisRubricScoreByRoom.has(rubricResult.data.roomId) ||
          thisRubricScoreByRoom.get(rubricResult.data.roomId)! <
            rubricResult.data.score
        ) {
          thisRubricScoreByRoom.set(
            rubricResult.data.roomId,
            rubricResult.data.score
          )
        }
      })

      // append the thisRubricScoreByRoom value to the respective
      // rubricScoresByRoom value array

      thisRubricScoreByRoom.forEach((score, roomId) => {
        if (!rubricScoresByRoom.has(roomId)) {
          rubricScoresByRoom.set(roomId, new Map([[rubric, [score]]]))
        } else if (!rubricScoresByRoom.get(roomId)!.has(rubric)) {
          rubricScoresByRoom.get(roomId)!.set(rubric, [score])
        } else {
          rubricScoresByRoom.get(roomId)!.get(rubric)!.push(score)
        }
      })
    })

    // calculate the score for each room
    // the numerator is the sum of the max scores for each rubric
    // the denominator is the number of rubrics

    rubricScoresByRoom.forEach((rubrics, roomId) => {
      let numerator = 0
      let denominator = 0

      rubrics.forEach((scores, rubric) => {
        scores.forEach((score) => {
          numerator += Math.pow(
            score *
              RubricTypeClass.getRubricTypeNormalizationFactor(
                rubric.rubricType!
              ),

            RubricTypeClass.getRubricWeightingExponent(rubric.rubricType!)
          )

          denominator += Math.pow(
            RubricTypeClass.getRubricExpectedGroupScore(rubric.rubricType!) *
              RubricTypeClass.getRubricTypeNormalizationFactor(
                rubric.rubricType!
              ),

            RubricTypeClass.getRubricWeightingExponent(rubric.rubricType!)
          )
        })
      })

      // if denominator is less than 1 then set it to 1

      if (denominator < 1) {
        denominator = 1
      }

      const roomState = this.roomStates.models.find(
        (element) => element.id === roomId
      )

      if (roomState) {
        roomRubricScores.set(roomState, Math.min(numerator / denominator, 1))
      }
    })

    return roomRubricScores
  }

  @computed
  get groupRubricHistogramWithRoomStates(): Record<string, RoomState[]> {
    const histogram: { [key: string]: RoomState[] } = {
      '20 %': [],
      '40 %': [],
      '60 %': [],
      '80 %': [],
      '100 %': [],
    }

    this.rubricScores.forEach((percentage, roomState) => {
      if (percentage < 0.2) {
        histogram['20 %'].push(roomState)
      } else if (percentage < 0.4) {
        histogram['40 %'].push(roomState)
      } else if (percentage < 0.6) {
        histogram['60 %'].push(roomState)
      } else if (percentage < 0.8) {
        histogram['80 %'].push(roomState)
      } else {
        histogram['100 %'].push(roomState)
      }
    })

    // Sort the lists by values descending

    Object.entries(histogram).forEach(([, values]) => {
      values.sort((a, b) => {
        const aIndex = this.rubricScores.get(a) ?? 0

        const bIndex = this.rubricScores.get(b) ?? 0

        return bIndex - aIndex
      })
    })

    return histogram
  }

  // create a map just like the one above but with just the length of
  // the value array and the same key
  @computed
  get groupRubricHistogram(): Record<string, number> {
    const histogram: { [key: string]: number } = {}
    // loop over groupRubricHistogramWithRoomStates and put length of value array in histogram
    Object.entries(this.groupRubricHistogramWithRoomStates).forEach(
      ([key, value]) => {
        histogram[key] = value.length
      }
    )
    return histogram
  }

  // count the number of rubricResponses we have
  @computed
  get rubricResponseCount(): number {
    return this.roomStateRubricResults.length
  }

  @action
  setGradeBookSortingStrategy = (
    strategy: typeof this.gradeBookSortingStrategy,
    order: typeof this.gradeBookSortingOrder
  ) => {
    this.gradeBookSortingStrategy = strategy
    this.gradeBookSortingOrder = order
  }

  @action
  setGradeBookNameSortMode = (mode: NameSortMode) => {
    this.gradeBookNameSortMode = mode
  }

  @action
  setQuestionsNameSortMode = (mode: NameSortMode) => {
    this.questionsNameSortMode = mode
  }

  @action
  setQuestionsNameSortOrder = (order: 'asc' | 'desc') => {
    this.questionsNameSortOrder = order
  }

  fetchUserProfilesForCorreCSV = (userIds: string[]) => {
    if (!this.repository.breakoutUser?.isCorre)
      throw new Error('method not supported for non-corre users')
    return fetchUserProfiles(this.repository, { userIds })
  }

  getRubricResultsForRoom = (roomId: string) => {
    const results: Map<SlideRubric, Array<RoomStateRubricResult>> = new Map()
    for (const [key, value] of this.rubrics.entries()) {
      const rubricResults = value.filter(
        (result) => result.data.roomId === roomId
      )
      if (rubricResults.length > 0) results.set(key, rubricResults)
    }
    return results
  }

  getRubricResultDetailsForRoom = (roomId: string) => {
    return this.roomStateRubricResultDetails.models.filter((result) => {
      return result.data.roomId === roomId
    })
  }

  async publishAssignment() {
    return await updateSectionAssignmentState(this.repository, {
      sectionId: this.sectionId,
      assignmentId: this.assignmentId,
      state: AssignmentState.active,
    })
  }

  async updateAssignment(
    params: Omit<
      Parameters<typeof updateSectionAssignment>[1],
      'sectionId' | 'assignmentId' | 'isGrouping'
    >
  ) {
    await updateSectionAssignment(this.repository, {
      sectionId: this.sectionId,
      assignmentId: this.assignmentId,
      ...params,
    })
  }

  async updateGradingLineItemId({
    ltiGradingLineItemId,
  }: {
    ltiGradingLineItemId: string
  }) {
    await updateSectionAssignmentLTIGradingLineItemId(this.repository, {
      sectionId: this.sectionId,
      assignmentId: this.assignmentId,
      ltiGradingLineItemId,
    })
  }

  async updateGradingScalars({
    assignmentGradingScalars,
  }: {
    assignmentGradingScalars?: FirestoreSectionAssignment['assignmentGradingScalars']
  }) {
    await updateSectionAssignmentGradingScalars(this.repository, {
      sectionId: this.sectionId,
      assignmentId: this.assignmentId,
      assignmentGradingScalars,
    })
  }

  async markAsActive() {
    return updateSectionAssignmentState(this.repository, {
      sectionId: this.sectionId,
      assignmentId: this.assignmentId,
      state: AssignmentState.active,
    })
  }

  addUserToGroup = async (params: {
    userId: string
    roomId: string
    isGroupLeader: boolean
    roleId?: string
    batch?: ReturnType<typeof writeBatch>
  }) => {
    await roomStateAddUser(this.repository.firestore, params)
  }

  async setPeerReviewQuestions(params: {
    questions: Array<{
      prQuestion: string
      prDescription: string
      prOrder: number
    }>
  }) {
    return setPeerReviewQuestionsForAssignment(this.repository, {
      sectionId: this.sectionId,
      assignmentId: this.assignmentId,
      questions: params.questions,
    })
  }

  async savePeerReviewConfigFromFormFields({
    peerReviewsEnabled,
    peerReviewCriteria,
    peerReviewExcludesSelfReview,
    peerReviewsHiddenFromReviewees,
  }: {
    peerReviewsEnabled: boolean
    peerReviewCriteria: Array<{ prQuestion: string; prDescription: string }>
    peerReviewExcludesSelfReview?: boolean
    peerReviewsHiddenFromReviewees?: boolean
  }) {
    if (this.hasBeenStarted) {
      throw new Error(
        'Peer review settings cannot be modified after sessions have started'
      )
    }
    if (peerReviewsEnabled) {
      const questions = peerReviewCriteria.map((c, index) => ({
        prQuestion: c.prQuestion,
        prDescription: c.prDescription,
        prOrder: index,
      }))
      await this.setPeerReviewQuestions({ questions })
      await this.updateAssignment({
        peerReviewExcludesSelfReview,
        peerReviewsHiddenFromReviewees,
      })
    } else {
      await this.setPeerReviewQuestions({ questions: [] })
      await this.updateAssignment({
        peerReviewExcludesSelfReview: false,
        peerReviewsHiddenFromReviewees: false,
      })
    }
  }

  async removeUserFromGroup(params: {
    userId: string
    roomId: string
    batch?: ReturnType<typeof writeBatch>
  }) {
    await leaveRoom(this.repository.firestore, { ...params, clearRole: true })
  }

  async removeUserRoleInGroup(params: { userId: string; roomId: string }) {
    await removeUserRole(this.repository.firestore, params)
  }

  async sendTestEmail() {
    await sendTestInstructorEmail(this.repository, {
      sectionId: this.sectionId,
      assignmentId: this.assignmentId,
    })
  }

  sortQuestionsMap(questions: Map<SlideQuestion, Array<RoomStateAnswer>>) {
    const sortedQuestions: typeof questions = new Map()
    const slideQuestions = Array.from(questions.keys())
    const slideIds = this.slideDeckSlides.models.map((s) => s.id)
    const slideQuestionsSorted = sortQuestions({
      questions: slideQuestions,
      slideIds,
    })
    for (const question of slideQuestionsSorted) {
      const answers = questions.get(question) ?? []
      sortedQuestions.set(question, answers)
    }
    return sortedQuestions
  }

  /**
   * to be consumed by the Array.sort function
   */
  private sortAssignmentGroupData(
    a: AssignmentGroupData,
    b: AssignmentGroupData
  ) {
    if (!a.roomState || a.roomState.isEmpty) return 1
    if (!b.roomState || b.roomState.isEmpty) return -1

    // if room state has no users, sort it last
    // if both room states have no users, continue
    if (a.roomState.userCount !== b.roomState.userCount) {
      if (a.roomState.data.userIds.length === 0) return 1
      if (b.roomState.data.userIds.length === 0) return -1
    }

    if (a.roomStateStatus !== b.roomStateStatus) {
      return a.roomStateStatus - b.roomStateStatus
    }
    if (
      a.roomState?.data.activeSlideChangedAt &&
      b.roomState?.data.activeSlideChangedAt
    ) {
      return (
        a.roomState.data.activeSlideChangedAt.getTime() -
        b.roomState.data.activeSlideChangedAt.getTime()
      )
    }
    if (a.roomState && b.roomState) {
      return a.roomState.roomStateName.localeCompare(
        b.roomState.roomStateName,
        undefined,
        {
          numeric: true,
          sensitivity: 'base',
        }
      )
    }
    return 0
  }

  deleteGroup = async (roomId: string) => {
    try {
      await deleteRoomState(this.repository, {
        roomId,
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
      })
      await this.touchAssignment()
    } catch (error) {
      console.error('Failed to delete group:', error)
      throw error
    }
  }

  get emptyGroupsCount(): number {
    return this.roomStates.models.filter(
      (roomState) => roomState.userIds.length === 0
    ).length
  }

  clearEmptyGroups = async () => {
    const emptyRoomIds = this.roomStates.models
      .filter((roomState) => roomState.userIds.length === 0)
      .map((roomState) => roomState.id)

    if (emptyRoomIds.length === 0) return

    await deleteRoomStates(
      this.repository,
      emptyRoomIds.map((roomId) => ({
        roomId,
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
      }))
    )

    await this.touchAssignment()
  }

  async createGroups(
    numberOfGroups: number,
    scheduledAt?: Date,
    groupNames?: string[]
  ) {
    const existingNames = this.roomStates.models.map(
      (room) => room.roomStateName
    )
    if (!groupNames) {
      groupNames = generateUniqueGroupNames(
        this.repository,
        numberOfGroups,
        existingNames
      )
    }

    const result = await bulkCreateRoomStates(this.repository.firestore, {
      count: numberOfGroups,
      baseData: {
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
        slideDeckId: this.assignment.data.slideDeckId,
        scheduledAt,
        deletable: false,
      },
      roomStateNames: groupNames,
    })

    await this.touchAssignment()

    return result
  }

  @action
  /** populates the map `slideDecksByIdForOtherAssignments` */
  async fetchSlideDecksForAssignments(assignments: SectionAssignment[]) {
    const slideDeckIds = assignments.reduce((acc, assignment) => {
      if (!assignment.data.slideDeckId) return acc
      acc.add(assignment.data.slideDeckId)
      return acc
    }, new Set<string>())

    // check if slide decks are in the map
    const slideDecks = Array.from(slideDeckIds)
    const slideDecksToStartFetching = slideDecks.filter((slideDeckId) => {
      if (!this._slideDeckByIdForOtherAssignments.has(slideDeckId)) {
        this._slideDeckByIdForOtherAssignments.set(
          slideDeckId,
          SlideDeck.empty(this.repository)
        )
        return true
      }
      return false
    })

    // fetch slide decks that are not in the map
    const fetchedSlideDecks = await fetchSlideDecksById(this.repository, {
      slideDeckIds: slideDecksToStartFetching,
    })

    // set the fetched slide decks into the map
    fetchedSlideDecks.forEach((slideDeck) => {
      this._slideDeckByIdForOtherAssignments.set(slideDeck.id, slideDeck)
    })
  }

  /*
   * NOTE(danielmartin0): Below this boundary I started building some new conventions, others welcome to follow if they want.
   *
   * 1) Update methods should be specific to each UI, no generic 'updateAssignment'.
   * 2) Deletion operations in update methods should require an explicit deleteField() sentinel rather than 'null'.
   * 3) Avoid nested classes (namely AssignmentGroupData from the Dart port), it can lead to tangled code.
   * 4) If computed properties depend on certain data having been loaded, they should be wrapped in the 'guardOnLoaded' helper so that typescript _forces_ the value to be loaded before being used.
   */

  @observable currentRoomStateId?: string

  // Observable time tracker for role issue timer-based recomputation
  @observable private _recomputeRoomStatesWithStatusTimerTick = 0
  private _recomputeRoomStatesWithStatusTimer: NodeJS.Timeout | undefined =
    undefined

  @action
  async setCurrentRoomStateId(roomStateId?: string) {
    this.currentRoomStateId = roomStateId
  }

  getChatController(roomId: string): InstructorChatController {
    if (!this.chatControllers.has(roomId)) {
      const controller = new InstructorChatController({
        repository: this.repository,
        roomId,
      })
      controller.initialize()
      this.chatControllers.set(roomId, controller)
    }
    return this.chatControllers.get(roomId)!
  }

  @computed
  get roomStatesWithStatus() {
    return guardOn(
      this.roomStates.isLoaded &&
        this.roles.isLoaded &&
        this.assignment.isLoaded,
      () => {
        // access the timer tick to ensure recomputation when timers fire
        this._recomputeRoomStatesWithStatusTimerTick

        let recomputeInMs: number | undefined

        const roomStatesWithStatus: RoomStateWithStatusAndRoleIssueAndMinGroupSizeIssue[] =
          this.roomStates.models.map((room) => {
            const status = room.getRoomStateStatus(
              this.assignment.expiresAt,
              this.section.data.sectionState,
              this.slideDeckSlides.length,
              this.assignment.data.assignmentState,
              this.roomStateEngagements.models.some(
                (e) => e.data.roomId === room.id
              )
            )

            const issueResponse = getRoleIssueForRoomState({
              roomState: room,
              roles: this.roles.models,
            })

            const roleIssue = issueResponse ? issueResponse.issue : null

            // Check for minimum group size issue
            const minGroupSizeIssueResponse = getMinGroupSizeIssue({
              roomState: room,
              minGroupSize: this.assignment.minGroupSize,
            })
            const minGroupSizeIssue = minGroupSizeIssueResponse
              ? minGroupSizeIssueResponse.issue
              : null

            // Filter out unassignedUsers role issues unless it's been more than 10 seconds since last update
            let shouldUseRoleIssue = true

            if (roleIssue === 'unassignedUsers' && room.data.updatedAt) {
              const now = Date.now()
              const timeSinceUpdate = now - room.data.updatedAt.getTime()
              const tenSecondsInMs = 10 * 1000

              if (timeSinceUpdate < tenSecondsInMs) {
                // if unassigned users, don't return this issue until 10 seconds have passed
                // we want to give the trigger a chance to auto-assign before notifying the instructor
                shouldUseRoleIssue = false
                // schedule recompute to run after the remaining time has passed
                const timeUntilRecompute = tenSecondsInMs - timeSinceUpdate

                // use greater value to avoid multiple rooms scheduling too frequently
                if (timeUntilRecompute > (recomputeInMs || 0))
                  recomputeInMs = timeUntilRecompute
              }
            }

            // Clone the room object, preserving property descriptors and prototype
            const clonedRoom = Object.create(
              Object.getPrototypeOf(room),
              Object.getOwnPropertyDescriptors(room)
            )

            // Define the new 'status' property on the cloned object
            Object.defineProperty(clonedRoom, 'status', {
              value: status,
              enumerable: true,
              writable: true,
              configurable: true,
            })

            // define the new 'roleIssue' property on the cloned object
            Object.defineProperty(clonedRoom, 'roleIssue', {
              value: shouldUseRoleIssue ? roleIssue : null,
              enumerable: true,
              writable: true,
              configurable: true,
            })

            // define the new 'minGroupSizeIssue' property on the cloned object
            Object.defineProperty(clonedRoom, 'minGroupSizeIssue', {
              value: minGroupSizeIssue,
              enumerable: true,
              writable: true,
              configurable: true,
            })

            return clonedRoom
          })

        // if we have a recomputeInMs value, set a timer to update the tick
        if (recomputeInMs) {
          clearTimeout(this._recomputeRoomStatesWithStatusTimer)
          this._recomputeRoomStatesWithStatusTimer = setTimeout(() => {
            this._recomputeRoomStatesWithStatusTimerTick++
          }, recomputeInMs)
        }

        return sortRoomStatesWithStatus(roomStatesWithStatus)
      }
    )
  }

  @computed
  get currentRoomWithStatus() {
    return guardOn(!this.roomStatesWithStatus.isLoading, () => {
      return this.roomStatesWithStatus.value?.find(
        (room) => room.id === this.currentRoomStateId
      )
    })
  }

  @computed
  get usersMarkedAsAttendedCurrentRoom() {
    return guardOn(
      !this.currentRoomWithStatus.isLoading &&
        this.currentRoomWithStatus.value?.userIds
        ? true
        : false,
      () => {
        return this.currentRoomWithStatus.value!.userIds.filter((userId) => {
          return this.roomStateEngagements.models.some(
            (engagement) =>
              engagement.data.userId === userId && !engagement.isEmpty
          )
        })
      }
    )
  }

  @computed
  get usersNotInGroupLoaded() {
    return guardOn(this.roomStates.isLoaded && this.section.isLoaded, () => {
      return this.usersNotInGroup
    })
  }

  @computed
  get gradeBookUserData(): Array<InstructorAssignmentGradeBookUserData> {
    const slideIdToSlidePosition = this.slideDeckSlides.models.reduce<
      Record<string, number>
    >(
      (acc, slide, index) => {
        acc[slide.id] = index + 1 //position is index + 1
        return acc
      },
      // set pre-meeting to 0th slide
      { [PRE_MEETING_QUESTION_SLIDE_ID]: 0 }
    )

    // Get all quiz answers in a flat array
    const quizAnswers = Array.from(this.quizQuestions.values()).flat()

    const usersHaveTakenQuizInRoom: Map<
      string,
      Set<string>
    > = quizAnswers.reduce((acc, answer) => {
      const roomId = answer.data.roomId
      const userId = answer.data.userId
      const roomEntry = acc.get(roomId)
      if (!roomEntry) {
        acc.set(roomId, new Set([userId]))
      } else {
        roomEntry.add(userId)
      }
      return acc
    }, new Map<string, Set<string>>())

    const hasLmsConflictMap: Map<string, LMSConflict> = new Map()

    this.lmsConflicts.models.forEach((conflict) => {
      const key = `${conflict.data.userId}-${conflict.data.roomId}`
      hasLmsConflictMap.set(key, conflict)
    })

    // Build a map of userId -> most recent roomId based on roomStartedAt
    const userLatestRoomMap: Map<string, string> = new Map()
    this.assignmentGroupDataSortedWithNotInGroup.forEach((cur) => {
      if (!cur.roomState) return
      const roomStartedAt = cur.roomState.data.roomStartedAt?.getTime() ?? 0
      cur.users.forEach((user) => {
        const existingRoomId = userLatestRoomMap.get(user.id)
        if (!existingRoomId) {
          userLatestRoomMap.set(user.id, cur.roomState!.id)
        } else {
          // Find the existing room's start time
          const existingRoom =
            this.assignmentGroupDataSortedWithNotInGroup.find(
              (g) => g.roomState?.id === existingRoomId
            )
          const existingStartedAt =
            existingRoom?.roomState?.data.roomStartedAt?.getTime() ?? 0
          if (roomStartedAt > existingStartedAt) {
            userLatestRoomMap.set(user.id, cur.roomState!.id)
          }
        }
      })
    })

    return this.assignmentGroupDataSortedWithNotInGroup
      .reduce<Array<InstructorAssignmentGradeBookUserData>>((acc, cur) => {
        cur.users.forEach((user) => {
          if (!cur.roomState) return

          const scoresBySlideForUser = Array.from(
            cur.quizScoreByUserIdAndSlideId.get(user.id) ??
              new Map<string, number>()
          )
          const scoresBySlidePosition = scoresBySlideForUser
            .map(([slideId, score]) => ({
              slideNumber: slideIdToSlidePosition[slideId],
              score,
            }))
            .sort((a, b) => a.slideNumber - b.slideNumber)

          const hasAnswer =
            usersHaveTakenQuizInRoom.get(cur.roomState.id)?.has(user.id) ??
            false

          const quizScore = {
            total: cur.quizScore.get(user.id) ?? 0,
            bySlide: scoresBySlidePosition,
          }

          // Check if there's an LMS conflict for this user and room
          const roomStateId = cur.roomState?.id
          const conflictKey = `${user.id}-${roomStateId}`

          const lmsConflict = hasLmsConflictMap.get(conflictKey)

          // Check if this is the latest room for this user
          const isLatestRoomForUser =
            userLatestRoomMap.get(user.id) === roomStateId

          const baseUserData = {
            user,
            groupData: cur,
            quizScore,
            attendance: cur.attendanceScoreForUser(user.id) === 1,
            completion: cur.engagementScoreForUser(user.id),
            room: cur.roomState,
            hasTakenQuiz: hasAnswer,
            isLatestRoomForUser,
          }

          acc.push(
            lmsConflict
              ? { ...baseUserData, hasLMSConflict: true, lmsConflict }
              : { ...baseUserData, hasLMSConflict: false }
          )
        })
        return acc
      }, [])
      .sort(this.gradeBookSorter)
      .filter(({ user, room }) => {
        if (!this.userAndRoomFilters.length) return true
        const userName = user.fullName.toLowerCase()
        const roomName = room.roomStateName.toLowerCase()
        return this.userAndRoomFilters.some((filter) => {
          const search = filter.trim().toLowerCase()
          return userName.includes(search) || roomName.includes(search)
        })
      })
  }

  userMarkedAsAttendedSomeRoom = (userId: string) => {
    return this.roomStatesWithStatus.value?.some(
      (room) =>
        room.userIds.some((id) => id === userId) &&
        this.roomStateEngagements.models.some(
          (engagement) =>
            engagement.data.userId === userId &&
            !engagement.isEmpty &&
            engagement.data.roomId === room.id
        )
    )
  }

  async updateRoomName({ roomId, name }: { roomId: string; name: string }) {
    return updateRoomState(this.repository.firestore, roomId, {
      roomStateName: name,
    })
  }

  async updateRoomScheduledTime({ scheduledAt }: { scheduledAt: DateTime }) {
    if (!this.currentRoomStateId) {
      throw new Error('No room state id found')
    }

    return updateRoomState(this.repository.firestore, this.currentRoomStateId, {
      scheduledAt: scheduledAt.toJSDate(),
    })
  }

  async clearRoomScheduledTime() {
    if (!this.currentRoomStateId) {
      throw new Error('No room state id found')
    }

    return updateRoomState(this.repository.firestore, this.currentRoomStateId, {
      scheduledAt: deleteField(),
    })
  }

  async updateAllRoomScheduledTimes({
    scheduledAt,
    roomStateIds,
  }: {
    scheduledAt: DateTime
    roomStateIds: string[]
  }) {
    await bulkScheduleRoomStates(this.repository, {
      roomStateIds,
      scheduledAt: scheduledAt.toJSDate(),
    })
  }

  async bulkUpdateAddRoomUsers(
    updates: Array<{ roomId: string; userIds: string[] }>
  ) {
    await bulkUpdateRoomUsers(this.repository.firestore, {
      updates,
      append: true,
    })
  }

  async assignUsersToRandomGroupsV2({
    fillExistingGroups,
    stagedState,
  }: {
    fillExistingGroups: boolean
    stagedState?: {
      stagedChangesUsersToRooms: Map<string, string>
      setStagedChangesUsersToRooms: (assignments: Map<string, string>) => void
      setStagedNewRooms: (rooms: { roomName: string }[]) => void
    }
  }) {
    // Construct knowledge of the existing rooms and memberships (including staged rooms and memberships if necessary)
    const currentRoomsAndUsers: (
      | {
          id: string
          name: string
          users: { stagedOrCurrent: boolean; id: string }[]
          available: boolean
          alreadyExists: true
        }
      | {
          id: string
          name: string
          users: { stagedOrCurrent: boolean; id: string }[]
          available: true
          alreadyExists: false
        }
    )[] = []

    this.roomStates.models.forEach((room) => {
      currentRoomsAndUsers.push({
        alreadyExists: true,
        id: room.id,
        name: room.roomStateName,
        users: [...room.data.userIds].map((id) => ({
          stagedOrCurrent: true,
          id,
        })),
        available: !room.didStart,
      })
    })

    stagedState?.stagedChangesUsersToRooms.forEach((roomIdentifier, userId) => {
      const existingRoom = currentRoomsAndUsers.find((m) =>
        m.alreadyExists ? m.id === roomIdentifier : m.name === roomIdentifier
      )
      if (existingRoom) {
        existingRoom.users.push({ stagedOrCurrent: true, id: userId })
      } else {
        // This must be a staged room
        currentRoomsAndUsers.push({
          id: doc(this.repository.firestore, 'room_state').id,
          name: roomIdentifier,
          users: [{ stagedOrCurrent: true, id: userId }],
          available: true,
          alreadyExists: false,
        })
      }
    })

    const max = this.assignment.maxGroupSize
    const min = this.assignment.data.groupingSizeMinimum ?? 1

    if (min > max) throw new Error('Unexpected grouping parameters')

    const unassignedUsers = [...this.section.students].filter(
      (student) =>
        !currentRoomsAndUsers.some((m) =>
          m.users.some((user) => user.id === student.id)
        )
    )

    shuffle(unassignedUsers)

    if (unassignedUsers.length === 0) return

    // First pass: Fill existing groups up to desired size.
    if (fillExistingGroups) {
      const availableRooms = currentRoomsAndUsers
        .filter((room) => room.available)
        .sort((a, b) => a.users.length - b.users.length)

      for (const room of availableRooms) {
        while (
          room.users.length < max &&
          unassignedUsers.length > 0 &&
          // don't add to this group unless we can hit the min size
          unassignedUsers.length + room.users.length >= min
        ) {
          room.users.push({
            stagedOrCurrent: false,
            id: unassignedUsers.pop()!.id,
          })
        }
      }
    }

    // Second pass: If there are fewer students remaining than can form a group, distribute them over existing groups.
    if (fillExistingGroups && unassignedUsers.length < min) {
      let availableRooms = currentRoomsAndUsers.filter((room) => room.available)

      while (unassignedUsers.length > 0 && availableRooms.length > 0) {
        availableRooms = availableRooms.sort(
          (a, b) => a.users.length - b.users.length
        )

        // Find the first available room that hasn't reached max capacity
        const targetRoom = availableRooms.find(
          (room) => room.users.length < max
        )

        // add to room if we have one, and keep checking
        if (targetRoom) {
          targetRoom.users.push({
            stagedOrCurrent: false,
            id: unassignedUsers.pop()!.id,
          })

          continue
        }

        // else break out the loop
        break
      }
    }

    // Third pass: Create new groups for the remaining users.
    const plannedGroups: { userIds: string[] }[] = []

    if (unassignedUsers.length > 0) {
      // if we have unassigned users lets try and create buckets of users with them
      let viableGroupSizeWithAcceptableRemainder: number | null = null

      while (!viableGroupSizeWithAcceptableRemainder) {
        for (let groupSize = max; groupSize >= min; groupSize--) {
          const remainder = unassignedUsers.length % groupSize
          if (remainder === 0 || (remainder >= min && remainder <= max)) {
            viableGroupSizeWithAcceptableRemainder = groupSize
            break
          }
        }

        // we have an answer to the grouping problem, move one
        if (viableGroupSizeWithAcceptableRemainder) break

        // pull one user out of a group that can afford to lose one, and then continue
        let didPullUser = false
        for (const room of currentRoomsAndUsers) {
          // if we're at min threshold, we can't pull anyone out
          // if it's one user, we can pull them and just not create the virtual room
          if (room.users.length > 1 && room.users.length <= min) continue
          const userToPull = room.users.find((user) => !user.stagedOrCurrent)
          if (userToPull) {
            room.users = room.users.filter((user) => user.id !== userToPull.id)
            unassignedUsers.push(
              this.section.students.find((s) => s.id === userToPull.id)!
            )
            didPullUser = true
            break
          }
        }
        if (!didPullUser) break
      }

      if (!viableGroupSizeWithAcceptableRemainder)
        throw new Error(
          `Remaining participants cannot be distributed into groups given size constraints (min: ${min}, max: ${max})`
        )

      // split into groups of viableGroupSizeWithAcceptableRemainder
      while (unassignedUsers.length > 0) {
        const groupUserIds: string[] = []
        for (
          let i = 0;
          i < viableGroupSizeWithAcceptableRemainder &&
          unassignedUsers.length > 0;
          i++
        ) {
          groupUserIds.push(unassignedUsers.pop()!.id)
        }
        plannedGroups.push({ userIds: groupUserIds })
      }
    }

    const newRoomNames = generateUniqueGroupNames(
      this.repository,
      plannedGroups.length,
      currentRoomsAndUsers.map((room) => room.name)
    )

    const plannedGroupsWithNames = plannedGroups.map((group, index) => ({
      ...group,
      roomName: newRoomNames[index],
    }))

    // If we're in staging mode, use the callbacks. Otherwise, actually perform the updates.
    if (stagedState) {
      stagedState.setStagedNewRooms([
        ...currentRoomsAndUsers
          .filter((room) => !room.alreadyExists)
          .map((room) => ({
            roomName: room.name,
          })),
        ...plannedGroupsWithNames,
      ])

      const updatedStagedChanges = new Map(
        stagedState.stagedChangesUsersToRooms
      )

      for (const room of currentRoomsAndUsers) {
        for (const { stagedOrCurrent, id: userId } of room.users) {
          if (stagedOrCurrent) continue
          updatedStagedChanges.set(
            userId,
            room.alreadyExists ? room.id : room.name
          )
        }
      }
      for (const group of plannedGroupsWithNames) {
        for (const userId of group.userIds) {
          updatedStagedChanges.set(userId, group.roomName)
        }
      }
      stagedState.setStagedChangesUsersToRooms(updatedStagedChanges)
    } else {
      if (plannedGroups.length > 0) {
        // first, create new rooms in firestore
        const newRooms = await bulkCreateRoomStates(this.repository.firestore, {
          count: plannedGroups.length,
          baseData: {
            sectionId: this.sectionId,
            assignmentId: this.assignmentId,
            slideDeckId: this.assignment.data.slideDeckId,
            deletable: false,
          },
          roomStateNames: newRoomNames,
        })

        for (let i = 0; i < newRooms.length; i++) {
          currentRoomsAndUsers.push({
            alreadyExists: true,
            id: newRooms[i].id,
            name: newRooms[i].id,
            users: plannedGroups[i].userIds.map((id) => ({
              stagedOrCurrent: false,
              id,
            })),
            available: true,
          })
        }
      }

      const roomUpdates: { roomId: string; usersToAdd: string[] }[] = []

      for (const room of currentRoomsAndUsers) {
        const usersToAppend = room.users
          .filter((u) => !u.stagedOrCurrent)
          .map((u) => u.id)

        if (!usersToAppend.length) continue

        roomUpdates.push({ roomId: room.id, usersToAdd: usersToAppend })
      }

      if (roomUpdates.length === 0) return

      await bulkUpdateRoomUsers(
        this.repository.firestore,
        roomUpdates.map((update) => ({
          userIds: update.usersToAdd,
          roomId: update.roomId,
          append: true,
        }))
      )
    }
  }

  async assignUsersToRandomGroups({
    fillExistingGroups,
    stagedState,
  }: {
    fillExistingGroups: boolean
    stagedState?: {
      stagedChangesUsersToRooms: Map<string, string>
      setStagedChangesUsersToRooms: (assignments: Map<string, string>) => void
      setStagedNewRooms: (rooms: { roomName: string }[]) => void
    }
  }) {
    if (this.roles.models.length > 0)
      return await this.assignUsersToRandomGroupsV2({
        fillExistingGroups,
        stagedState,
      })

    // Construct knowledge of the existing rooms and memberships (including staged rooms and memberships if necessary)
    const currentRoomsAndUsers: (
      | {
          id: string
          name: string
          userIds: string[]
          available: boolean
          alreadyExists: true
        }
      | {
          name: string
          userIds: string[]
          available: true
          alreadyExists: false
        }
    )[] = []

    this.roomStates.models.forEach((room) => {
      currentRoomsAndUsers.push({
        alreadyExists: true,
        id: room.id,
        name: room.roomStateName,
        userIds: [...room.data.userIds],
        available: !room.didStart,
      })
    })

    stagedState?.stagedChangesUsersToRooms.forEach((roomIdentifier, userId) => {
      const existingRoom = currentRoomsAndUsers.find((m) =>
        m.alreadyExists ? m.id === roomIdentifier : m.name === roomIdentifier
      )
      if (existingRoom) {
        existingRoom.userIds.push(userId)
      } else {
        // This must be a staged room
        currentRoomsAndUsers.push({
          name: roomIdentifier,
          userIds: [userId],
          available: true,
          alreadyExists: false,
        })
      }
    })

    const max = this.assignment.maxGroupSize
    const desired = this.assignment.desiredGroupSize
    const min = this.assignment.data.groupingSizeMinimum ?? 1

    if (min > desired || min > max || desired > max) {
      throw new Error('Unexpected grouping parameters')
    }

    const roomUpdatesNeeded: { roomIdentifier: string; userIds: string[] }[] =
      []

    const unassignedUsers = [...this.section.students].filter(
      (student) =>
        !currentRoomsAndUsers.some((m) => m.userIds.includes(student.id))
    )

    shuffle(unassignedUsers)

    if (unassignedUsers.length === 0) return

    // First pass: Fill existing groups up to desired size.
    if (fillExistingGroups) {
      const availableRooms = currentRoomsAndUsers
        .filter((room) => room.available)
        .sort((a, b) => a.userIds.length - b.userIds.length)

      for (const room of availableRooms) {
        const currentUsers = [...room.userIds]
        while (currentUsers.length < desired && unassignedUsers.length > 0) {
          currentUsers.push(unassignedUsers.pop()!.id)
        }
        if (currentUsers.length !== room.userIds.length) {
          roomUpdatesNeeded.push({
            roomIdentifier: room.alreadyExists ? room.id : room.name,
            userIds: currentUsers.filter(
              (userId) => !room.userIds.includes(userId)
            ),
          })
        }
      }
    }

    // Second pass: If there are fewer students remaining than can form a group, distribute them over existing groups.
    if (fillExistingGroups && unassignedUsers.length < min) {
      let availableRooms = currentRoomsAndUsers.filter((room) => room.available)

      while (unassignedUsers.length > 0 && availableRooms.length > 0) {
        availableRooms = availableRooms.sort(
          (a, b) => a.userIds.length - b.userIds.length
        )

        const unassignedUser = unassignedUsers.pop()

        availableRooms[0].userIds.push(unassignedUser!.id)
      }
    }

    // Third pass: Create new groups for the remaining users.
    const plannedGroups: { userIds: string[] }[] = []
    while (unassignedUsers.length > 0) {
      let numberOfUsersToTake = Math.min(desired, unassignedUsers.length)
      const remainingUsers = unassignedUsers.length - numberOfUsersToTake
      if (remainingUsers > 0 && remainingUsers < min) {
        numberOfUsersToTake = unassignedUsers.length - min
      }

      const usersForThisGroup = unassignedUsers.splice(0, numberOfUsersToTake)
      plannedGroups.push({ userIds: usersForThisGroup.map((u) => u.id) })
    }

    const newRoomNames = generateUniqueGroupNames(
      this.repository,
      plannedGroups.length,
      currentRoomsAndUsers.map((room) => room.name)
    )

    const plannedGroupsWithNames = plannedGroups.map((group, index) => ({
      ...group,
      roomName: newRoomNames[index],
    }))

    // If we're in staging mode, use the callbacks. Otherwise, actually perform the updates.
    if (stagedState) {
      stagedState.setStagedNewRooms([
        ...currentRoomsAndUsers
          .filter((room) => !room.alreadyExists)
          .map((room) => ({
            roomName: room.name,
          })),
        ...plannedGroupsWithNames,
      ])

      const updatedStagedChanges = new Map(
        stagedState.stagedChangesUsersToRooms
      )
      for (const update of roomUpdatesNeeded) {
        for (const userId of update.userIds) {
          updatedStagedChanges.set(userId, update.roomIdentifier)
        }
      }
      for (const group of plannedGroupsWithNames) {
        for (const userId of group.userIds) {
          updatedStagedChanges.set(userId, group.roomName)
        }
      }
      stagedState.setStagedChangesUsersToRooms(updatedStagedChanges)
    } else {
      if (plannedGroups.length > 0) {
        const newRooms = await bulkCreateRoomStates(this.repository.firestore, {
          count: plannedGroups.length,
          baseData: {
            sectionId: this.sectionId,
            assignmentId: this.assignmentId,
            slideDeckId: this.assignment.data.slideDeckId,
            deletable: false,
          },
          roomStateNames: newRoomNames,
        })

        for (let i = 0; i < newRooms.length; i++) {
          roomUpdatesNeeded.push({
            roomIdentifier: newRooms[i].id,
            userIds: plannedGroups[i].userIds,
          })
        }
      }

      if (roomUpdatesNeeded.length > 0) {
        await bulkUpdateRoomUsers(this.repository.firestore, {
          updates: roomUpdatesNeeded.map((update) => ({
            userIds: update.userIds,
            roomId: update.roomIdentifier,
          })),
          append: false,
        })
      }
    }
  }

  private gradeBookSorter = (
    a: InstructorAssignmentGradeBookUserData,
    b: InstructorAssignmentGradeBookUserData
  ): number => {
    const [first, second] =
      this.gradeBookSortingOrder === 'asc' ? [a, b] : [b, a]

    // if not sorting on full name, always place empty rooms as the end of the list
    if (
      this.gradeBookSortingStrategy !== 'fullName' &&
      (a.room.isEmpty || b.room.isEmpty)
    ) {
      if (a.room.isNotEmpty) return -1
      if (b.room.isNotEmpty) return 1

      // if both rooms empty, sort by name while ignoring sort direction
      return compareNames(
        a.user.nameParts,
        b.user.nameParts,
        this.gradeBookNameSortMode
      )
    }

    switch (this.gradeBookSortingStrategy) {
      case 'fullName': {
        return compareNames(
          first.user.nameParts,
          second.user.nameParts,
          this.gradeBookNameSortMode
        )
      }
      case 'group': {
        return first.room.simpleRoomName.localeCompare(
          second.room.simpleRoomName
        )
      }
      case 'quizScore': {
        return first.quizScore.total - second.quizScore.total
      }
      case 'attendance': {
        const [firstNum, secondNum] = [
          first.attendance ? 1 : 0,
          second.attendance ? 1 : 0,
        ]
        return firstNum - secondNum
      }
      case 'completion': {
        return first.completion - second.completion
      }
      case 'criticalThinking': {
        const [firstScore, secondScore] = [
          first.groupData.totalRubricScoreForUserFloat(first.user.id) ?? 0,
          second.groupData.totalRubricScoreForUserFloat(second.user.id) ?? 0,
        ]
        return firstScore - secondScore
      }
      case 'finalGrade': {
        const scalars = this.assignment.data.assignmentGradingScalars
        const [firstScore, secondScore] = [
          first.groupData.scalarRawScoreDataForUser(first.user.id, scalars)
            ?.totalScore ?? 0,
          second.groupData.scalarRawScoreDataForUser(second.user.id, scalars)
            ?.totalScore ?? 0,
        ]
        return firstScore - secondScore
      }
    }
  }

  roomStateNameExists(candidate: string, skipRoomId?: string) {
    this.roomStates
    const existingNames = this.roomStates.models
      // skip the current room (if applicable)
      .filter((r) => r.id !== skipRoomId)
      .map((roomState) => roomState.roomStateName)

    return existingNames.includes(candidate)
  }

  scalarRawScoreDataForUser(
    userId: string,
    scalars: SectionAssignment['data']['assignmentGradingScalars'],
    { nullIfNoScalars }: { nullIfNoScalars: boolean } = {
      nullIfNoScalars: true,
    }
  ) {
    // Find the user's group data - they should only be in one group
    const userGroupData = this.assignmentGroupData.find((groupData) =>
      groupData.groupMembers.includes(userId)
    )

    if (!userGroupData) {
      // User not in any group, return null or zero scores
      if (nullIfNoScalars && !scalars) return null
      return {
        totalScore: 0,
        participationScore: 0,
        attendanceScore: 0,
        quizScore: 0,
        totalRubricScore: 0,
      }
    }

    // Use the group data to calculate scores
    return userGroupData.scalarRawScoreDataForUser(userId, scalars, {
      nullIfNoScalars,
    })
  }

  touchAssignment() {
    return touchAssignment(this.repository, {
      sectionId: this.sectionId,
      assignmentId: this.assignmentId,
    })
  }
}

export class ApplyGroupsToFutureAssignmentsEligibilityError extends Error {
  constructor(kind: 'rooms' | 'assignment', assignmentId: string) {
    super(
      `Eligibility error for applying groups to future assignments: ${kind} not eligible for assignment ${assignmentId}`
    )
    this.name = 'ApplyGroupsToFutureAssignmentsEligibilityError'
  }
}

export class AssignmentGroupData {
  public absentUsers: PublicUser[]
  public engagementData: Map<string, RoomStateEngagement>
  public groupLeader?: string
  public groupName?: string // this should render as `not in a group` if undefined
  public groupMembers: string[]
  public groupSortingAnswerScores: Map<string, number>
  public roomStateStatus: RoomStateStatus
  public roomState?: RoomState
  public quizScore: Map<string, number>
  public quizScoreByUserIdAndSlideId: Map<string, Map<string, number>>
  public rubricResults: Map<string, Map<SlideRubric, RoomStateRubricResult[]>>
  public summary?: RoomStateSummary
  public sortingQuizScores: Map<string, Map<string, number>>
  public sortingQuestions: Map<string, SlideQuestion>
  public users: PublicUser[]
  public usersHaveTakenQuiz: Set<string>
  public rubricTypes?: PromptRubricType[] = []
  public assignmentRubrics?: Map<string, FirestoreAssignmentRubric>

  constructor({
    absentUsers = [],
    groupName,
    groupMembers,
    roomState,
    summary,
    engagementData = new Map(),
    groupLeader,
    groupSortingAnswerScores = new Map(),
    quizScore = new Map(),
    quizScoreByUserIdAndSlideId = new Map(),
    sortingQuizScores = new Map(),
    sortingQuestions = new Map(),
    roomStateStatus,
    rubricResults = new Map(),
    users = [],
    usersHaveTakenQuiz = new Set<string>(),
    promptRubricTypes,
    assignmentRubrics,
  }: {
    absentUsers?: PublicUser[]
    groupName: string
    groupMembers: string[]
    roomState: RoomState
    summary: RoomStateSummary
    engagementData?: Map<string, RoomStateEngagement>
    groupLeader?: string
    groupSortingAnswerScores?: Map<string, number>
    quizScore?: Map<string, number>
    quizScoreByUserIdAndSlideId?: Map<string, Map<string, number>>
    sortingQuizScores?: Map<string, Map<string, number>>
    sortingQuestions?: Map<string, SlideQuestion>
    roomStateStatus: RoomStateStatus
    rubricResults?: Map<string, Map<SlideRubric, RoomStateRubricResult[]>>
    users: PublicUser[]
    usersHaveTakenQuiz: Set<string>
    promptRubricTypes?: PromptRubricType[]
    assignmentRubrics?: Map<string, FirestoreAssignmentRubric>
  }) {
    this.absentUsers = absentUsers
    this.groupName = groupName
    this.groupMembers = groupMembers
    this.roomState = roomState
    this.summary = summary
    this.engagementData = engagementData
    this.groupLeader = groupLeader
    this.groupSortingAnswerScores = groupSortingAnswerScores
    this.quizScore = quizScore
    this.quizScoreByUserIdAndSlideId = quizScoreByUserIdAndSlideId
    this.sortingQuizScores = sortingQuizScores
    this.sortingQuestions = sortingQuestions
    this.roomStateStatus = roomStateStatus
    this.rubricResults = rubricResults
    this.users = users
    this.usersHaveTakenQuiz = usersHaveTakenQuiz
    this.rubricTypes = promptRubricTypes
    this.assignmentRubrics = assignmentRubrics
  }

  getResultsByUserIdForRubric(rubric: SlideRubric) {
    const resultsPerUser = new Map<string, RoomStateRubricResult>()
    for (const [userId, rubricResults] of this.rubricResults.entries()) {
      const rubricResult = rubricResults.get(rubric)
      if (rubricResult && rubricResult.length > 0) {
        resultsPerUser.set(userId, rubricResult[0])
      }
    }
    return resultsPerUser
  }

  // `totalRubricScore` internal scalar (on assignment)
  // `Critical Thinking` for copy/UI
  totalRubricScoreForUserFloat(userId: string) {
    return calculateTotalRubricScoreForUserFloat({
      userId,
      rubricResults: this.rubricResults,
      rubricTypes: this.rubricTypes,
      roomState: this.roomState,
      assignmentRubrics: this.assignmentRubrics,
    })
  }

  // `attendanceScore` internal scalar (on assignment)
  // `Attendance` for copy/UI
  attendanceScoreForUser(userId: string) {
    return calculateAttendanceScoreForUser({
      userId,
      engagementData: this.engagementData,
    })
  }

  // `participationScore` internal scalar (on assignment)
  // `Completion` for copy/UI
  engagementScoreForUser(userId: string) {
    return calculateEngagementScoreForUser({
      userId,
      engagementData: this.engagementData,
      medianEngagementDuration: this.medianEngagementDuration,
    })
  }

  quizScoreForUser(userId: string) {
    return calculateQuizScoreForUser({
      userId,
      quizScores: this.quizScore,
    })
  }

  scalarRawScoreDataForUser(
    userId: string,
    scalars: SectionAssignment['data']['assignmentGradingScalars'],
    { nullIfNoScalars }: { nullIfNoScalars: boolean } = {
      nullIfNoScalars: true,
    }
  ) {
    return calculateScalarRawScoreDataForUser({
      userId,
      scalars,
      quizScores: this.quizScore,
      rubricResults: this.rubricResults,
      rubricTypes: this.rubricTypes,
      roomState: this.roomState,
      engagementData: this.engagementData,
      medianEngagementDuration: this.medianEngagementDuration,
      assignmentRubrics: this.assignmentRubrics,
      nullIfNoScalars,
    })
  }

  scalarScoreDataForUser(
    userId: string,
    scalars: SectionAssignment['data']['assignmentGradingScalars'],
    options: { nullIfNoScalars: boolean } = { nullIfNoScalars: true }
  ) {
    const scores = this.scalarRawScoreDataForUser(userId, scalars, options)

    if (!scores) return null

    const scoresAsStrings: {
      participationScore: string
      quizScore?: string | null
      totalRubricScore?: string
      totalScore: string
      attendanceScore: string
    } = {
      participationScore: '',
      attendanceScore: '',
      totalScore: '',
    }

    const keys: Array<keyof typeof scores> = [
      'quizScore',
      'totalRubricScore',
      'participationScore',
      'attendanceScore',
      'totalScore',
    ]

    for (const key of keys) {
      if (!(key in scores)) continue
      const value = scores[key]
      // Handle null quiz score (no MC questions in slide deck)
      if (value === null) {
        scoresAsStrings[key as 'quizScore'] = null
        continue
      }
      scoresAsStrings[key] = Math.ceil(value! * 100) + '%'
    }

    return scoresAsStrings
  }

  get medianEngagementDuration() {
    return calculateMedianEngagementDuration(
      Array.from(this.engagementData.values())
    )
  }
}

function roomStateName({
  roomState,
  section,
  usersMap,
}: {
  roomState: RoomState
  section: Section
  usersMap: Record<string, PublicUser>
}) {
  let groupName = roomState.roomStateName
  if (!groupName) {
    // set group name to the name of the first group leader
    // is the first groupLeaderUserIds in the usersMapIncludingInstructorAndRemovedStudents?
    // if not check the instructor's userId and get the full name
    if (roomState.groupLeaderUserIds.length) {
      const groupLeaderId = roomState.groupLeaderUserIds[0]
      const groupLeader = usersMap[groupLeaderId]
      if (groupLeader) {
        groupName = groupLeader.fullName
      } else if (roomState.groupLeaderUserIds.includes(section.instructor.id)) {
        groupName = section.instructor.fullName
      }
    }
  }
  return groupName ?? 'Group has no name'
}

/**
 * checks if the assignment is eligible as a copy target
 * does NOT consider if the assignments room are eligible or not
 * see `assignmentIsEligibleForCopyingRoomsTo`
 */
function assignmentIsEligibleForCopyingRoomsTo({
  assignment,
  currentAssignmentId,
  currentSectionId,
}: {
  assignment: SectionAssignment
  currentAssignmentId: string
  currentSectionId: string
}) {
  if (
    assignment.id === currentAssignmentId || // must be other assignment
    assignment.data.sectionId !== currentSectionId || // must be in the same section
    !assignment.isActive || // must be published
    assignment.isExpired || // must not be expired
    assignment.data.groupingType === AssignmentGroupingType.automaticRandom
  ) {
    return false
  }
  return true
}

/**
 * checks if the assignments room are eligible as a copy target
 * does NOT consider assignment eligibility, see `assignmentIsEligibleForCopyingRoomsTo`
 */
function assignmentRoomsAreEligibleForCopying({
  targetAssignment,
  targetAssignmentRooms,
  currentAssignmentRooms,
}: {
  targetAssignment: SectionAssignment
  targetAssignmentRooms: RoomState[]
  currentAssignmentRooms: RoomState[]
}) {
  const roomsHaveNoUsersAndDidNotStart = targetAssignmentRooms.every(
    (room) => room.userIds.length === 0 && !room.didStart
  )

  // must have no rooms, empty un-started rooms
  if (!targetAssignmentRooms.length || roomsHaveNoUsersAndDidNotStart)
    return true

  const { assignedAtIsInFuture } = targetAssignment

  // if we have populated rooms, we can't perform a copy regardless of current config if its exposed to students
  if (!assignedAtIsInFuture) return false

  // last check, exclude assignments that have identical config to the source assignment
  const hasIdenticalConfiguration = targetAssignmentRooms.every((room) => {
    // if the room is empty, ignore it for comparison as its not relevant
    if (!room.userIds.length && !room.groupLeaderUserIds.length) return true

    // if the room has users, check if there's a room in source assignment with the
    // same name / userIds / groupLeader userIds
    return currentAssignmentRooms.some((currentRoom) => {
      return (
        currentRoom.data.roomStateName === room.data.roomStateName &&
        arraysEqualUnordered(currentRoom.data.userIds, room.userIds) &&
        arraysEqualUnordered(
          currentRoom.data.groupLeaderUserIds,
          room.groupLeaderUserIds
        )
      )
    })
  })

  if (hasIdenticalConfiguration) return false

  return true
}

function assignmentRoleConstraintsAreEligibleForCopyingTo({
  sourceAssignment,
  targetRoles,
  currentRooms,
}: {
  sourceAssignment: SectionAssignment
  currentRooms: RoomState[]
  targetRoles: SlideDeckRole[]
}) {
  // if the target has no roles, we can't possibly have invalid constraints
  if (!targetRoles.length) return true

  // get the role constraints for the target assignment
  const tConstraints = getGroupSizeConstraintsFromRoles(targetRoles)

  // constraints are not restrictive, no further checks
  if (tConstraints.min === 1 && tConstraints.max === RoomState.maxAllowedUsers)
    return true

  // max role size falls outside constraints, block copy here
  if (
    sourceAssignment.data.groupingSizeMaximum &&
    sourceAssignment.data.groupingSizeMaximum > tConstraints.max
  )
    return false

  let smallestUserCount: number | undefined
  let largestUserCount: number | undefined

  for (const room of currentRooms) {
    if (!room.userCount) continue // do not care about rooms with no users
    if (!smallestUserCount || room.userCount < smallestUserCount) {
      smallestUserCount = room.userCount
    }
    if (!largestUserCount || room.userCount > largestUserCount) {
      largestUserCount = room.userCount
    }
  }

  // we should never arrive here as this means there are no rooms to copy
  // return false so we block if we happen to make an error in the future
  if (!smallestUserCount || !largestUserCount) return false

  // current rooms are outside of target constraints
  // lets skip the copy in this case
  if (
    smallestUserCount < tConstraints.min ||
    largestUserCount > tConstraints.max
  )
    return false

  return true
}

export type RubricResultWithUser = RoomStateRubricResult & { user: PublicUser }

export type InstructorAssignmentGradeBookSortingStrategy =
  | 'fullName'
  | 'group'
  | 'quizScore'
  | 'attendance'
  | 'completion'
  | 'criticalThinking'
  | 'finalGrade'

export type InstructorAssignmentGradeBookUserData = {
  user: PublicUser
  groupData: AssignmentGroupData
  quizScore: {
    total: number
    bySlide: { slideNumber: number; score: number }[]
  }
  hasTakenQuiz: boolean
  attendance: boolean
  completion: number
  room: RoomState
  isLatestRoomForUser: boolean
} & (
  | {
      hasLMSConflict: true
      lmsConflict: LMSConflict
    }
  | {
      hasLMSConflict: false
      lmsConflict?: undefined
    }
)

class GroupsExportBatchManager {
  private repository: FirebaseRepository
  private sectionId: string

  private currentBatch: WriteBatch
  private batchSize = 450 // 50 shy of limit
  private batches: WriteBatch[] = []
  private currentBatchOperationCount = 0

  constructor({
    repository,
    sectionId,
  }: {
    repository: FirebaseRepository
    sectionId: string
  }) {
    this.repository = repository
    this.sectionId = sectionId
    this.currentBatch = writeBatch(this.repository.firestore)
  }

  addUpdateAssignmentGroupingSizeToBatch = async ({
    assignment,
    groupingSize,
    groupingSizeMaximum,
    groupingSizeMinimum,
  }: {
    assignment: SectionAssignment
    groupingSize: number | undefined
    groupingSizeMaximum: number | undefined
    groupingSizeMinimum: number | undefined
  }) => {
    if (this.currentBatchOperationCount >= this.batchSize) {
      this.batches.push(this.currentBatch)
      this.currentBatch = writeBatch(this.repository.firestore)
      this.currentBatchOperationCount = 0
    }

    batchUpdateSectionAssignmentGroupingSize(this.repository, {
      sectionId: this.sectionId,
      assignmentId: assignment.id,
      batch: this.currentBatch,
      groupingSize,
      groupingSizeMaximum,
      groupingSizeMinimum,
    })
    this.currentBatchOperationCount += 1
  }

  addCopyRoomNameAndUsersToBatch = async ({
    userIds,
    groupLeaderUserIds,
    roomName,
    roomToUpdate,
    assignmentId,
    slideDeckId,
  }: {
    userIds: string[]
    groupLeaderUserIds: string[]
    roomName: string
    assignmentId: string
    slideDeckId: string
    roomToUpdate?: RoomState
  }) => {
    if (this.currentBatchOperationCount >= this.batchSize) {
      this.batches.push(this.currentBatch)
      this.currentBatch = writeBatch(this.repository.firestore)
      this.currentBatchOperationCount = 0
    }

    if (roomToUpdate) {
      batchUpdateRoomState(
        this.repository.firestore,
        this.currentBatch,
        roomToUpdate.id,
        {
          roomStateName: roomName,
          userIds: userIds,
          groupLeaderUserIds: groupLeaderUserIds,
          scheduledAt: deleteField(),
          updatedAt: serverTimestamp(),
        }
      )
    } else {
      batchCreateRoomState(this.repository.firestore, this.currentBatch, {
        roomStateName: roomName,
        userIds: userIds,
        groupLeaderUserIds: groupLeaderUserIds,
        sectionId: this.sectionId,
        assignmentId,
        scheduled: undefined,
        deletable: false,
        slideDeckId,
      })
    }
    this.currentBatchOperationCount += 1
  }

  commit = async () => {
    if (this.currentBatchOperationCount > 0) {
      this.batches.push(this.currentBatch)
      this.currentBatch = writeBatch(this.repository.firestore)
      this.currentBatchOperationCount = 0
    }
    for (const batch of this.batches) {
      await batch.commit()
    }
  }
}

/**
 * determines if two string arrays are equal, ignoring order
 * only works for string arrays with unique values
 */
function arraysEqualUnordered(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const setA = new Set(a)
  for (const val of b) {
    if (!setA.has(val)) return false
  }
  return true
}

type QuizQuestionsBySlide = Array<{
  slide: SlideModel | 'pre-meeting-quiz' | 'no-slide'
  quizQuestions: Array<{ question: SlideQuestion; answers: RoomStateAnswer[] }>
}>
